export default {
  sections: [
    {
      name: 'Adjective',
      slug: 'adjective',
      topics: [
        {
          name: 'All Every Whole',
          slug: 'all-every-whole',
          rule: {
            slug: 'all-every-whole',
            title: 'All, Every, Whole',
            description:
              'These words are used to talk about groups or complete things. Use <code>all</code> for entire groups or things, <code>every</code> for each item in a group, and <code>whole</code> for something complete.',
            tables: [
              {
                caption: "When to use 'all', 'every', and 'whole'",
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'all',
                    'Entire group or thing',
                    '<code>All</code> students were present.'
                  ],
                  [
                    'every',
                    'Each item in a group',
                    '<code>Every</code> student has a book.'
                  ],
                  [
                    'whole',
                    'Complete single thing',
                    'She read the <code>whole</code> book.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>All</code> the cookies are gone.',
              '<code>Every</code> morning, I drink coffee.',
              'He spent the <code>whole</code> day at the beach.'
            ]
          },
          error: null
        },
        {
          name: 'Comparative Superlative',
          slug: 'comparative-superlative',
          rule: {
            slug: 'comparative-superlative',
            title: 'Comparative and Superlative Adjectives',
            description:
              "Comparative adjectives compare two things, using <code>-er</code> or <code>more</code>. Superlative adjectives compare more than two things, using <code>-est</code> or <code>most</code>. The choice depends on the adjective's syllable count.",
            tables: [
              {
                caption: 'Forming comparative and superlative adjectives',
                columns: ['Adjective Type', 'Comparative', 'Superlative'],
                rows: [
                  [
                    'One syllable',
                    '<code>-er</code> (e.g., taller)',
                    '<code>-est</code> (e.g., tallest)'
                  ],
                  [
                    'Two syllables ending in -y',
                    '<code>-ier</code> (e.g., happier)',
                    '<code>-iest</code> (e.g., happiest)'
                  ],
                  [
                    'Two or more syllables',
                    '<code>more</code> (e.g., more beautiful)',
                    '<code>most</code> (e.g., most beautiful)'
                  ]
                ]
              }
            ],
            examples: [
              'She is <code>taller</code> than her brother.',
              'This is the <code>happiest</code> day of my life.',
              'He is <code>more intelligent</code> than his friend.',
              "That was the <code>most interesting</code> book I've read."
            ]
          },
          error: null
        },
        {
          name: 'Comparative',
          slug: 'comparative',
          rule: {
            slug: 'comparative',
            title: 'Comparatives',
            description:
              'Comparatives are used to compare two things. We often add <code>-er</code> to short adjectives, or use <code>more</code> with longer adjectives. Remember to use <code>than</code> to show the difference.',
            tables: [
              {
                caption: 'Forming comparatives',
                columns: ['Adjective Type', 'Rule', 'Example'],
                rows: [
                  [
                    'Short adjectives',
                    'Add <code>-er</code>',
                    '<code>taller</code> than me'
                  ],
                  [
                    "Adjectives ending in 'y'",
                    'Change <code>y</code> to <code>i</code> and add <code>-er</code>',
                    '<code>happier</code> than before'
                  ],
                  [
                    'Long adjectives',
                    'Use <code>more</code> before the adjective',
                    '<code>more beautiful</code> than a rose'
                  ]
                ]
              },
              {
                caption: 'Irregular comparatives',
                columns: ['Adjective', 'Comparative Form', 'Example'],
                rows: [
                  ['good', 'better', '<code>better</code> than expected'],
                  ['bad', 'worse', '<code>worse</code> than yesterday'],
                  ['far', 'farther', '<code>farther</code> than the last time']
                ]
              }
            ],
            examples: [
              'She is <code>taller</code> than her brother.',
              'This book is <code>more interesting</code> than the other one.',
              'His performance was <code>better</code> than last year.'
            ]
          },
          error: null
        },
        {
          name: 'Ed Ing Adjectives',
          slug: 'ed-ing-adjectives',
          rule: {
            slug: 'ed-ing-adjectives',
            title: 'Ed and Ing Adjectives',
            description:
              'Adjectives ending in <code>-ed</code> describe how someone feels, while adjectives ending in <code>-ing</code> describe the thing causing the feeling. Use them to express emotions and reactions.',
            tables: [
              {
                caption: 'Difference between -ed and -ing adjectives',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  ['-ed', 'Describes feelings', 'She is <code>bored</code>.'],
                  [
                    '-ing',
                    'Describes the cause of feelings',
                    'The movie is <code>boring</code>.'
                  ],
                  ['-ed', 'Use with people', 'He felt <code>tired</code>.'],
                  [
                    '-ing',
                    'Use with things',
                    'The journey is <code>tiring</code>.'
                  ]
                ]
              }
            ],
            examples: [
              'I am <code>excited</code> about the trip.',
              'The news was <code>exciting</code>.',
              'She is <code>frustrated</code> with the puzzle.',
              'The puzzle is <code>frustrating</code>.'
            ]
          },
          error: null
        },
        {
          name: 'Order of Adjectives',
          slug: 'order-of-adjectives',
          rule: {
            slug: 'order-of-adjectives',
            title: 'Order of Adjectives',
            description:
              'When using more than one adjective before a noun, they should follow a specific order. Typically, adjectives are ordered as: <code>opinion</code>, <code>size</code>, <code>age</code>, <code>shape</code>, <code>color</code>, <code>origin</code>, <code>material</code>, <code>purpose</code>, and then the <code>noun</code>.',
            tables: [
              {
                caption: 'Order of adjectives before a noun',
                columns: ['Order', 'Type', 'Example'],
                rows: [
                  ['1st', 'Opinion', '<code>beautiful</code>'],
                  ['2nd', 'Size', '<code>big</code>'],
                  ['3rd', 'Age', '<code>new</code>'],
                  ['4th', 'Shape', '<code>round</code>'],
                  ['5th', 'Color', '<code>red</code>'],
                  ['6th', 'Origin', '<code>French</code>'],
                  ['7th', 'Material', '<code>wooden</code>'],
                  ['8th', 'Purpose', '<code>writing</code>']
                ]
              }
            ],
            examples: [
              'She has a <code>beautiful big old</code> house.',
              'Look at that <code>small round red</code> ball.',
              'He bought a <code>luxurious new Italian</code> car.'
            ]
          },
          error: null
        },
        {
          name: 'Other Another Others',
          slug: 'other-another-others',
          rule: {
            slug: 'other-another-others',
            title: 'Other, Another, Others',
            description:
              'Use <code>another</code> for one more of the same kind. Use <code>other</code> to refer to different items, often in a group. Use <code>others</code> when talking about more than one item or person.',
            tables: [
              {
                caption: 'How to use other, another, and others',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'another',
                    'One more of the same type',
                    'I will have <code>another</code> cup of coffee.'
                  ],
                  [
                    'other',
                    'Different or remaining item',
                    'I have <code>other</code> plans.'
                  ],
                  [
                    'others',
                    'More than one item or person',
                    'Some students are here, <code>others</code> are not.'
                  ]
                ]
              }
            ],
            examples: [
              'She needs <code>another</code> pen.',
              'Do you have <code>other</code> questions?',
              'Some people liked the movie, <code>others</code> did not.'
            ]
          },
          error: null
        },
        {
          name: 'Quantifiers',
          slug: 'quantifiers',
          rule: {
            slug: 'quantifiers',
            title: 'Quantifiers',
            description:
              'Quantifiers are words that come before nouns to show the amount or quantity. Use <code>some</code> and <code>any</code> for unspecified amounts, <code>much</code> and <code>many</code> for larger quantities.',
            tables: [
              {
                caption: 'Common quantifiers and their usage',
                columns: ['Quantifier', 'Usage', 'Example'],
                rows: [
                  [
                    'some',
                    'Used in positive sentences',
                    'I have <code>some</code> books.'
                  ],
                  [
                    'any',
                    'Used in negative and questions',
                    'Do you have <code>any</code> questions?'
                  ],
                  [
                    'much',
                    'Used with uncountable nouns',
                    "He doesn't have <code>much</code> time."
                  ],
                  [
                    'many',
                    'Used with countable nouns',
                    'There are <code>many</code> apples.'
                  ]
                ]
              }
            ],
            examples: [
              'She has <code>some</code> money.',
              "There aren't <code>any</code> cookies left.",
              'How <code>much</code> water do we need?',
              "I don't have <code>many</code> friends."
            ]
          },
          error: null
        },
        {
          name: 'Quite Rather',
          slug: 'quite-rather',
          rule: {
            slug: 'quite-rather',
            title: 'Quite and Rather',
            description:
              "Use <code>quite</code> and <code>rather</code> to show a degree of something, but they can have slightly different meanings. <code>Quite</code> can mean 'completely' or 'fairly', while <code>rather</code> often means 'more than expected'.",
            tables: [
              {
                caption: 'Differences between Quite and Rather',
                columns: ['Word', 'Meaning', 'Example'],
                rows: [
                  [
                    'quite',
                    'Completely or fairly',
                    'It is <code>quite</code> cold today.'
                  ],
                  [
                    'rather',
                    'More than expected',
                    'The movie was <code>rather</code> interesting.'
                  ],
                  [
                    'quite',
                    'Fairly or to some extent',
                    'She is <code>quite</code> happy with her job.'
                  ],
                  [
                    'rather',
                    'Used for emphasis',
                    'He is <code>rather</code> tall for his age.'
                  ]
                ]
              }
            ],
            examples: [
              'The book is <code>quite</code> good.',
              'It is <code>rather</code> unusual to see snow in April.',
              'She was <code>quite</code> surprised by the news.',
              'This is <code>rather</code> expensive for a small town.'
            ]
          },
          error: null
        },
        {
          name: 'Superlative',
          slug: 'superlative',
          rule: {
            slug: 'superlative',
            title: 'Superlative',
            description:
              'Superlatives describe the highest degree of something. They often end in <code>-est</code> or use <code>most</code> before the adjective. Use them to compare more than two things.',
            tables: [
              {
                caption: 'Forming Superlatives',
                columns: ['Type', 'Rule', 'Example'],
                rows: [
                  [
                    'One-syllable adjectives',
                    'Add <code>-est</code>',
                    '<code>fast</code> becomes <code>fastest</code>'
                  ],
                  [
                    'Two-syllable adjectives ending in <code>-y</code>',
                    'Change <code>-y</code> to <code>-iest</code>',
                    '<code>happy</code> becomes <code>happiest</code>'
                  ],
                  [
                    'Adjectives with two or more syllables',
                    'Use <code>most</code> before the adjective',
                    '<code>beautiful</code> becomes <code>most beautiful</code>'
                  ]
                ]
              }
            ],
            examples: [
              'Mount Everest is the <code>tallest</code> mountain.',
              'She is the <code>happiest</code> person I know.',
              'This is the <code>most interesting</code> book.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Basics',
      slug: 'basics',
      topics: [
        {
          name: 'Articles',
          slug: 'articles',
          rule: {
            slug: 'articles',
            title: 'Articles',
            description:
              'Articles come before nouns to show whether we mean something specific or general. Use <code>a</code> or <code>an</code> for general things, and <code>the</code> for specific ones.',
            tables: [
              {
                caption: 'When to use each article',
                columns: ['Article', 'Usage', 'Example'],
                rows: [
                  ['a', 'Before consonant sound', '<code>a</code> cat'],
                  [
                    'an',
                    'Before vowel sound',
                    '<code>an</code> apple, <code>an</code> hour'
                  ],
                  [
                    'the',
                    'Specific or known thing',
                    '<code>the</code> book on the table'
                  ]
                ]
              }
            ],
            examples: [
              'I saw <code>a</code> dog in the park.',
              'She bought <code>an</code> umbrella.',
              'Please close <code>the</code> window.'
            ]
          },
          error: null,
          ruleChecked: [1]
        },
        {
          name: 'Countable Uncountable',
          slug: 'countable-uncountable',
          rule: {
            slug: 'countable-uncountable',
            title: 'Countable and Uncountable Nouns',
            description:
              'Countable nouns are things we can count with numbers, like <code>apples</code> or <code>chairs</code>. Uncountable nouns are things we cannot count, like <code>water</code> or <code>rice</code>. Countable nouns usually have a plural form made with <code>-s</code> or <code>-es</code>.',
            tables: [
              {
                caption: 'Plural endings for countable nouns',
                columns: ['Ending', 'Rule', 'Example'],
                rows: [
                  [
                    '<code>-s</code>',
                    'Most nouns add <code>-s</code>',
                    '<code>cat</code> → <code>cats</code>'
                  ],
                  [
                    '<code>-es</code>',
                    'Add <code>-es</code> to words ending in <code>s</code>, <code>sh</code>, <code>ch</code>, <code>x</code>, <code>o</code>',
                    '<code>box</code> → <code>boxes</code>'
                  ],
                  [
                    'no plural',
                    'Uncountable nouns stay the same',
                    '<code>milk</code>, <code>rice</code>, <code>water</code>'
                  ]
                ]
              }
            ],
            examples: [
              'There are two <code>dogs</code> in the garden.',
              'She bought five <code>eggs</code>.',
              'I drink <code>milk</code> every morning.',
              'We need more <code>rice</code>.'
            ]
          },
          error: null,
          ruleChecked: [1]
        },
        {
          name: 'Do Does',
          slug: 'do-does',
          rule: {
            slug: 'do-does',
            title: 'Do and Does',
            description:
              'We use <code>do</code> and <code>does</code> in Present Simple questions and negatives. Use <code>do</code> with I, you, we, they, and <code>does</code> with he, she, it.',
            tables: [
              {
                caption: 'Using do and does',
                columns: ['Subject', 'Helping Verb', 'Example'],
                rows: [
                  [
                    'I / You / We / They',
                    'do',
                    "<code>Do</code> you like pizza? I <code>don't</code> like coffee."
                  ],
                  [
                    'He / She / It',
                    'does',
                    "<code>Does</code> she play tennis? She <code>doesn't</code> eat meat."
                  ]
                ]
              }
            ],
            examples: [
              '<code>Do</code> they work here?',
              '<code>Does</code> he live in London?',
              'I <code>don’t</code> like coffee.',
              'She <code>doesn’t</code> eat meat.'
            ]
          },
          error: null,
          ruleChecked: [1]
        },
        {
          name: 'Have Has',
          slug: 'have-has',
          rule: {
            slug: 'have-has',
            title: 'Have and Has',
            description:
              'We use <code>have</code> and <code>has</code> to show possession or to form perfect tenses. Use <code>have</code> with I, you, we, they, and <code>has</code> with he, she, it.',
            tables: [
              {
                caption: 'Using have and has',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'I / You / We / They',
                    'have',
                    'I <code>have</code> a car. We <code>have</code> a garden.'
                  ],
                  [
                    'He / She / It',
                    'has',
                    'He <code>has</code> a dog. She <code>has</code> a cat.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>have</code> two sisters.',
              'She <code>has</code> a new phone.',
              'They <code>have</code> been to London.',
              'He <code>has</code> already eaten breakfast.'
            ]
          },
          error: null,
          ruleChecked: [1]
        },
        {
          name: 'Mixed Prepositions',
          slug: 'mixed-prepositions',
          rule: {
            slug: 'mixed-prepositions',
            title: 'Mixed Prepositions',
            description:
              'Prepositions are words that show the relationship between a noun or pronoun and other words in a sentence. They can indicate time, place, direction, or method. Common prepositions include <code>in</code>, <code>on</code>, and <code>at</code>.',
            tables: [
              {
                caption: 'Common prepositions and their uses',
                columns: ['Preposition', 'Usage', 'Example'],
                rows: [
                  [
                    'in',
                    'Time (months, years)',
                    'She was born <code>in</code> July.'
                  ],
                  [
                    'on',
                    'Time (days, dates)',
                    'We will meet <code>on</code> Monday.'
                  ],
                  [
                    'at',
                    'Time (specific times)',
                    'The train leaves <code>at</code> 5 PM.'
                  ],
                  [
                    'in',
                    'Place (inside)',
                    'The books are <code>in</code> the box.'
                  ],
                  [
                    'on',
                    'Place (surface)',
                    'The book is <code>on</code> the table.'
                  ],
                  [
                    'at',
                    'Place (specific point)',
                    'She is <code>at</code> the door.'
                  ]
                ]
              }
            ],
            examples: [
              'He arrived <code>at</code> the station.',
              'They live <code>in</code> New York.',
              'The meeting is <code>on</code> Friday.',
              'Put the vase <code>on</code> the shelf.',
              'She works <code>in</code> an office.'
            ]
          },
          error: null
        },
        {
          name: 'Nationalities Countries',
          slug: 'nationalities-countries',
          rule: {
            slug: 'nationalities-countries',
            title: 'Nationalities and Countries',
            description:
              'Nationalities describe people from certain countries. Use a capital letter for both <code>countries</code> and <code>nationalities</code>. Nationalities often end in <code>-ian</code>, <code>-ese</code>, or <code>-ish</code>.',
            tables: [
              {
                caption: 'Common nationalities and their countries',
                columns: ['Country', 'Nationality', 'Example'],
                rows: [
                  ['France', 'French', 'She is <code>French</code>.'],
                  ['Japan', 'Japanese', 'He is <code>Japanese</code>.'],
                  ['Brazil', 'Brazilian', 'They are <code>Brazilian</code>.'],
                  ['Spain', 'Spanish', 'She speaks <code>Spanish</code>.'],
                  ['Italy', 'Italian', 'He loves <code>Italian</code> food.']
                ]
              }
            ],
            examples: [
              'I met a <code>Canadian</code> tourist.',
              'She is learning <code>Chinese</code>.',
              'His friend is <code>Mexican</code>.'
            ]
          },
          error: null
        },
        {
          name: 'Plurals',
          slug: 'plurals',
          rule: {
            slug: 'plurals',
            title: 'Plurals',
            description:
              'Plurals show that there is more than one of something. Most nouns add <code>-s</code> or <code>-es</code> to become plural. Some nouns have irregular forms.',
            tables: [
              {
                caption: 'Regular plural forms',
                columns: ['Ending', 'Rule', 'Example'],
                rows: [
                  ['-s', 'Most nouns', '<code>cats</code>'],
                  [
                    '-es',
                    'Nouns ending in <code>-ch</code>, <code>-sh</code>, <code>-s</code>, <code>-x</code>, <code>-z</code>',
                    '<code>boxes</code>'
                  ],
                  [
                    '-es',
                    'Nouns ending in <code>-o</code> after a consonant',
                    '<code>tomatoes</code>'
                  ]
                ]
              },
              {
                caption: 'Irregular plural forms',
                columns: ['Singular', 'Plural', 'Example'],
                rows: [
                  [
                    'child',
                    '<code>children</code>',
                    'The <code>children</code> are playing.'
                  ],
                  [
                    'man',
                    '<code>men</code>',
                    'There are two <code>men</code> in the room.'
                  ],
                  ['foot', '<code>feet</code>', 'He has big <code>feet</code>.']
                ]
              }
            ],
            examples: [
              'I have three <code>dogs</code>.',
              'She has many <code>wishes</code>.',
              'The <code>children</code> are happy.',
              'We saw two <code>geese</code> by the lake.'
            ]
          },
          error: null
        },
        {
          name: 'Prepositions of Place',
          slug: 'prepositions-of-place',
          rule: {
            slug: 'prepositions-of-place',
            title: 'Prepositions of Place',
            description:
              'Prepositions of place show the location of something. Common ones include <code>in</code>, <code>on</code>, and <code>at</code>. These words help us understand where things are in relation to each other.',
            tables: [
              {
                caption: 'Common prepositions of place',
                columns: ['Preposition', 'Usage', 'Example'],
                rows: [
                  [
                    'in',
                    'Inside a space',
                    'The cat is <code>in</code> the box.'
                  ],
                  [
                    'on',
                    'On a surface',
                    'The book is <code>on</code> the table.'
                  ],
                  ['at', 'Specific point', 'She is <code>at</code> the door.']
                ]
              },
              {
                caption: 'More examples with prepositions',
                columns: ['Preposition', 'Usage', 'Example'],
                rows: [
                  [
                    'under',
                    'Below something',
                    'The keys are <code>under</code> the mat.'
                  ],
                  [
                    'behind',
                    'At the back of',
                    'The car is <code>behind</code> the house.'
                  ],
                  [
                    'next to',
                    'Beside something',
                    'The lamp is <code>next to</code> the bed.'
                  ]
                ]
              }
            ],
            examples: [
              'The painting is <code>on</code> the wall.',
              'We are sitting <code>at</code> the table.',
              'The cat sleeps <code>in</code> the basket.',
              'The shoes are <code>under</code> the chair.'
            ]
          },
          error: null
        },
        {
          name: 'Prepositions of Time',
          slug: 'prepositions-of-time',
          rule: {
            slug: 'prepositions-of-time',
            title: 'Prepositions of Time',
            description:
              'Prepositions of time help us talk about when something happens. Use <code>at</code> for exact times, <code>on</code> for days and dates, and <code>in</code> for longer periods like months or years.',
            tables: [
              {
                caption: 'Using prepositions of time',
                columns: ['Preposition', 'Usage', 'Example'],
                rows: [
                  ['at', 'Exact time', "<code>at</code> 5 o'clock"],
                  ['on', 'Days and dates', '<code>on</code> Monday'],
                  ['on', 'Days and dates', '<code>on</code> July 4th'],
                  ['in', 'Months', '<code>in</code> April'],
                  ['in', 'Years', '<code>in</code> 2020'],
                  ['in', 'Parts of the day', '<code>in</code> the morning']
                ]
              }
            ],
            examples: [
              'I have a meeting <code>at</code> 3 PM.',
              'We will go hiking <code>on</code> Saturday.',
              'He was born <code>in</code> 1995.',
              'The party is <code>on</code> December 25th.',
              'She likes to jog <code>in</code> the evening.'
            ]
          },
          error: null
        },
        {
          name: 'There Is There Are',
          slug: 'there-is-there-are',
          rule: {
            slug: 'there-is-there-are',
            title: 'There Is / There Are',
            description:
              'Use <code>there is</code> for singular nouns and <code>there are</code> for plural nouns. They help to talk about things that exist or are present in a place.',
            tables: [
              {
                caption: "Using 'There is' and 'There are'",
                columns: ['Phrase', 'Usage', 'Example'],
                rows: [
                  [
                    'there is',
                    'Singular noun',
                    '<code>There is</code> a book on the table.'
                  ],
                  [
                    'there are',
                    'Plural noun',
                    '<code>There are</code> two cars outside.'
                  ],
                  [
                    'there is',
                    'Uncountable noun',
                    '<code>There is</code> some water in the glass.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>There is</code> a cat in the garden.',
              '<code>There are</code> many stars in the sky.',
              '<code>There is</code> a problem with the machine.',
              '<code>There are</code> chairs in the room.'
            ]
          },
          error: null
        },
        {
          name: 'There Was There Were',
          slug: 'there-was-there-were',
          rule: {
            slug: 'there-was-there-were',
            title: 'There Was / There Were',
            description:
              'Use <code>there was</code> for singular nouns and <code>there were</code> for plural nouns in the past tense. This helps to say that something existed or was present in the past.',
            tables: [
              {
                caption: "Using 'There Was' and 'There Were'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'there was',
                    'Singular noun',
                    '<code>There was</code> a cat on the roof.'
                  ],
                  [
                    'there were',
                    'Plural noun',
                    '<code>There were</code> three cats on the roof.'
                  ],
                  [
                    'there was',
                    'Uncountable noun',
                    '<code>There was</code> some water in the glass.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>There was</code> a big party last night.',
              '<code>There were</code> many people at the meeting.',
              '<code>There was</code> a lot of noise in the street.'
            ]
          },
          error: null
        },
        {
          name: 'This That These Those',
          slug: 'this-that-these-those',
          rule: {
            slug: 'this-that-these-those',
            title: 'This, That, These, Those',
            description:
              'We use <code>this</code> and <code>these</code> for things that are close to us. Use <code>that</code> and <code>those</code> for things that are far away. <code>This</code> and <code>that</code> are for singular, while <code>these</code> and <code>those</code> are for plural.',
            tables: [
              {
                caption: "Using 'This', 'That', 'These', 'Those'",
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  ['this', 'Singular, close', '<code>This</code> is my pen.'],
                  ['that', 'Singular, far', '<code>That</code> is a car.'],
                  [
                    'these',
                    'Plural, close',
                    '<code>These</code> are my shoes.'
                  ],
                  ['those', 'Plural, far', '<code>Those</code> are stars.']
                ]
              }
            ],
            examples: [
              '<code>This</code> is a nice dress.',
              'Can you see <code>that</code> airplane?',
              '<code>These</code> are my favorite books.',
              'Look at <code>those</code> birds in the sky.'
            ]
          },
          error: null
        },
        {
          name: 'To vs For',
          slug: 'to-vs-for',
          rule: {
            slug: 'to-vs-for',
            title: 'To vs For',
            description:
              'Use <code>to</code> when talking about movement or direction. Use <code>for</code> when expressing purpose or benefit. Learning when to use each can help in making sentences clear.',
            tables: [
              {
                caption: 'Differences between To and For',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'to',
                    'Indicating direction',
                    'She is going <code>to</code> the store.'
                  ],
                  [
                    'to',
                    'Indicating a recipient',
                    'I gave the book <code>to</code> him.'
                  ],
                  [
                    'for',
                    'Indicating purpose',
                    'This gift is <code>for</code> you.'
                  ],
                  [
                    'for',
                    'Indicating a time period',
                    'We waited <code>for</code> an hour.'
                  ]
                ]
              }
            ],
            examples: [
              'I am traveling <code>to</code> London.',
              'This cake is <code>for</code> her birthday.',
              'She sent a letter <code>to</code> her friend.',
              'He bought flowers <code>for</code> his mom.'
            ]
          },
          error: null
        },
        {
          name: 'Verb To Be',
          slug: 'verb-to-be',
          rule: {
            slug: 'verb-to-be',
            title: 'Verb To Be',
            description:
              'The verb <code>to be</code> is used to describe a state or condition. It changes form based on the subject. Common forms are <code>am</code>, <code>is</code>, and <code>are</code>.',
            tables: [
              {
                caption: "Forms of the verb 'to be'",
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  ['I', 'am', 'I <code>am</code> happy.'],
                  ['You', 'are', 'You <code>are</code> a student.'],
                  ['He/She/It', 'is', 'He <code>is</code> my friend.'],
                  ['We', 'are', 'We <code>are</code> ready.'],
                  ['They', 'are', 'They <code>are</code> here.']
                ]
              }
            ],
            examples: [
              'I <code>am</code> a teacher.',
              'She <code>is</code> very kind.',
              'We <code>are</code> at home.',
              'They <code>are</code> excited.'
            ]
          },
          error: null
        },
        {
          name: 'Was Were',
          slug: 'was-were',
          rule: {
            slug: 'was-were',
            title: 'Was / Were',
            description:
              'Use <code>was</code> and <code>were</code> as past tense forms of <code>be</code>. <code>Was</code> is for singular subjects like <code>I</code>, <code>he</code>, <code>she</code>, and <code>it</code>. <code>Were</code> is for plural subjects like <code>you</code>, <code>we</code>, and <code>they</code>.',
            tables: [
              {
                caption: 'Usage of was and were',
                columns: ['Form', 'Subject', 'Example'],
                rows: [
                  [
                    'was',
                    'I, he, she, it',
                    '<code>I was</code> at home yesterday.'
                  ],
                  [
                    'were',
                    'you, we, they',
                    '<code>You were</code> late for school.'
                  ],
                  ['was', 'singular noun', '<code>The dog was</code> hungry.'],
                  [
                    'were',
                    'plural noun',
                    '<code>The cats were</code> sleeping.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>He was</code> happy to see her.',
              '<code>We were</code> planning a trip.',
              '<code>She was</code> reading a book.',
              '<code>They were</code> playing outside.'
            ]
          },
          error: null
        },
        {
          name: 'Wh Questions',
          slug: 'wh-questions',
          rule: {
            slug: 'wh-questions',
            title: 'Wh Questions',
            description:
              'Wh questions are used to ask for specific information. They start with words like <code>what</code>, <code>where</code>, <code>when</code>, <code>who</code>, <code>why</code>, and <code>how</code>. These words help to find out more details about something.',
            tables: [
              {
                caption: 'Common Wh question words and their uses',
                columns: ['Wh Word', 'Usage', 'Example'],
                rows: [
                  [
                    'what',
                    'Asks for information about something',
                    '<code>What</code> is your name?'
                  ],
                  [
                    'where',
                    'Asks about a place',
                    '<code>Where</code> do you live?'
                  ],
                  [
                    'when',
                    'Asks about time',
                    '<code>When</code> is your birthday?'
                  ],
                  [
                    'who',
                    'Asks about a person',
                    '<code>Who</code> is your teacher?'
                  ],
                  [
                    'why',
                    'Asks for a reason',
                    '<code>Why</code> are you late?'
                  ],
                  [
                    'how',
                    'Asks about the manner or way',
                    '<code>How</code> do you make this?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>What</code> time is it?',
              '<code>Where</code> are you going?',
              '<code>Why</code> did you do that?',
              '<code>Who</code> is calling?',
              '<code>How</code> was your day?'
            ]
          },
          error: null
        },
        {
          name: 'When While',
          slug: 'when-while',
          rule: {
            slug: 'when-while',
            title: 'When vs. While',
            description:
              'Use <code>when</code> to talk about a specific time or event. Use <code>while</code> to discuss two actions happening at the same time. These words help connect ideas in a sentence.',
            tables: [
              {
                caption: 'Using when and while',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'when',
                    'Specific time or event',
                    'I was eating <code>when</code> he called.'
                  ],
                  [
                    'while',
                    'Two actions at the same time',
                    'She listens to music <code>while</code> studying.'
                  ],
                  [
                    'when',
                    'Event interrupts another',
                    'It started raining <code>when</code> we left.'
                  ],
                  [
                    'while',
                    'Ongoing action with another',
                    'He was reading <code>while</code> waiting for the bus.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>When</code> I arrived, the meeting had already started.',
              'He was cooking <code>while</code> she was setting the table.',
              'They were watching TV <code>when</code> the power went out.',
              'I usually listen to music <code>while</code> walking.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Be',
      slug: 'be',
      topics: [
        {
          name: 'Be Able To',
          slug: 'be-able-to',
          rule: {
            slug: 'be-able-to',
            title: 'Be Able To',
            description:
              "The phrase <code>be able to</code> is used to talk about ability in different tenses. It can replace <code>can</code> when you need to use other tenses like past or future. It's formed with the verb <code>be</code> followed by <code>able to</code> and the base form of another verb.",
            tables: [
              {
                caption: "Forms of 'Be Able To'",
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  [
                    'am/is/are able to',
                    'Present ability',
                    'She <code>is able to</code> swim.'
                  ],
                  [
                    'was/were able to',
                    'Past ability',
                    'He <code>was able to</code> finish the race.'
                  ],
                  [
                    'will be able to',
                    'Future ability',
                    'They <code>will be able to</code> attend the meeting.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>am able to</code> help you now.',
              'We <code>were able to</code> solve the problem yesterday.',
              'She <code>will be able to</code> visit next week.'
            ]
          },
          error: null
        },
        {
          name: 'Be Supposed To',
          slug: 'be-supposed-to',
          rule: {
            slug: 'be-supposed-to',
            title: 'Be Supposed To',
            description:
              'The phrase <code>be supposed to</code> is used to express an expectation or a duty. It often means that something is planned or that someone should do something.',
            tables: [
              {
                caption: "Forms of 'be supposed to'",
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  [
                    'am/is/are supposed to',
                    'Present expectation',
                    'She <code>is supposed to</code> start at 9 AM.'
                  ],
                  [
                    'was/were supposed to',
                    'Past expectation',
                    'They <code>were supposed to</code> arrive yesterday.'
                  ],
                  [
                    'will be supposed to',
                    'Future expectation',
                    'He <code>will be supposed to</code> handle it tomorrow.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>am supposed to</code> finish my homework by 6 PM.',
              'He <code>was supposed to</code> call me.',
              'They <code>are supposed to</code> follow the rules.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Conditional',
      slug: 'conditional',
      topics: [
        {
          name: 'First vs Second Conditional',
          slug: 'first-vs-second-conditional',
          rule: {
            slug: 'first-vs-second-conditional',
            title: 'First vs Second Conditional',
            description:
              'Conditionals help us talk about possibilities and hypothetical situations. The <code>first conditional</code> is used for real and possible future events. The <code>second conditional</code> is for unreal or unlikely present or future situations.',
            tables: [
              {
                caption: 'Forms of First and Second Conditional',
                columns: ['Conditional', 'Form', 'Example'],
                rows: [
                  [
                    'First Conditional',
                    '<code>if + present simple, will + base verb</code>',
                    'If it <code>rains</code>, we <code>will stay</code> inside.'
                  ],
                  [
                    'Second Conditional',
                    '<code>if + past simple, would + base verb</code>',
                    'If I <code>were</code> rich, I <code>would travel</code> the world.'
                  ]
                ]
              }
            ],
            examples: [
              'If she <code>studies</code>, she <code>will pass</code> the exam.',
              'If he <code>had</code> more time, he <code>would learn</code> Spanish.',
              'We <code>will go</code> to the beach if it <code>is</code> sunny.',
              'If I <code>won</code> the lottery, I <code>would buy</code> a car.'
            ]
          },
          error: null
        },
        {
          name: 'Mixed Conditionals',
          slug: 'mixed-conditionals',
          rule: {
            slug: 'mixed-conditionals',
            title: 'Mixed Conditionals',
            description:
              'Mixed conditionals are used when the time in the if-clause and the main clause are different. They often mix the second and third conditional forms. Use them to express past actions with present consequences, or present situations with past implications.',
            tables: [
              {
                caption: 'Types of Mixed Conditionals',
                columns: ['Conditional Type', 'Form', 'Example'],
                rows: [
                  [
                    'Past-Present',
                    'If + past perfect, ... would + base verb',
                    'If she <code>had studied</code>, she <code>would know</code> the answer.'
                  ],
                  [
                    'Present-Past',
                    'If + past simple, ... would have + past participle',
                    'If he <code>were</code> kinder, he <code>would have helped</code> us.'
                  ],
                  [
                    'Past-Future',
                    'If + past perfect, ... would + base verb',
                    'If they <code>had gone</code> earlier, they <code>would be</code> here now.'
                  ]
                ]
              }
            ],
            examples: [
              "If I <code>hadn't eaten</code> that much, I <code>would feel</code> better now.",
              'If he <code>were</code> more responsible, he <code>would have finished</code> the project.',
              'If they <code>had left</code> on time, they <code>would be</code> enjoying the concert now.'
            ]
          },
          error: null
        },
        {
          name: 'Second vs Third Conditional',
          slug: 'second-vs-third-conditional',
          rule: {
            slug: 'second-vs-third-conditional',
            title: 'Second vs Third Conditional',
            description:
              'Conditionals describe results of conditions. The <code>second conditional</code> talks about unreal or unlikely situations in the present or future. The <code>third conditional</code> refers to unreal situations in the past.',
            tables: [
              {
                caption: 'Second Conditional Structure',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'If + past simple, would + base verb',
                    'Unreal present/future',
                    'If I <code>were</code> rich, I <code>would travel</code> the world.'
                  ],
                  [
                    'If + past simple, could + base verb',
                    'Possibility in unreal situation',
                    'If he <code>studied</code> more, he <code>could pass</code> the exam.'
                  ]
                ]
              },
              {
                caption: 'Third Conditional Structure',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'If + past perfect, would have + past participle',
                    'Unreal past',
                    'If she <code>had known</code> earlier, she <code>would have come</code>.'
                  ],
                  [
                    'If + past perfect, could have + past participle',
                    'Missed possibility',
                    'If they <code>had left</code> on time, they <code>could have caught</code> the train.'
                  ]
                ]
              }
            ],
            examples: [
              'If I <code>had</code> more time, I <code>would read</code> more books.',
              'If we <code>had taken</code> the shortcut, we <code>would have arrived</code> earlier.',
              'If it <code>rained</code>, we <code>would stay</code> indoors.',
              'If she <code>had studied</code> harder, she <code>would have passed</code> the test.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Future Tense',
      slug: 'future-tense',
      topics: [
        {
          name: 'Be Going To',
          slug: 'be-going-to',
          rule: {
            slug: 'be-going-to',
            title: 'Be Going To',
            description:
              'The phrase <code>be going to</code> is used to talk about future plans or intentions. It shows what someone intends to do or what is likely to happen. Use the correct form of <code>be</code> with the subject, followed by <code>going to</code> and the base form of the verb.',
            tables: [
              {
                caption: "Forming 'Be Going To'",
                columns: ['Subject', "Form of 'Be'", 'Example'],
                rows: [
                  [
                    'I',
                    '<code>am going to</code>',
                    'I <code>am going to</code> visit my friend.'
                  ],
                  [
                    'You/We/They',
                    '<code>are going to</code>',
                    'They <code>are going to</code> play soccer.'
                  ],
                  [
                    'He/She/It',
                    '<code>is going to</code>',
                    'She <code>is going to</code> read a book.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>am going to</code> start a new hobby.',
              'He <code>is going to</code> study for the exam.',
              'They <code>are going to</code> travel to Japan next summer.',
              'We <code>are going to</code> have a picnic this weekend.'
            ]
          },
          error: null
        },
        {
          name: 'Future Continuous',
          slug: 'future-continuous',
          rule: {
            slug: 'future-continuous',
            title: 'Future Continuous',
            description:
              'The Future Continuous tense describes an action that will be happening at a specific time in the future. It uses <code>will be</code> + <code>verb-ing</code>. This tense shows that the action will be ongoing at a certain time.',
            tables: [
              {
                caption: 'Structure of Future Continuous',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Positive',
                    '<code>will be</code> + <code>verb-ing</code>',
                    'She <code>will be studying</code> at 8 PM.'
                  ],
                  [
                    'Negative',
                    '<code>will not be</code> + <code>verb-ing</code>',
                    'They <code>will not be playing</code> tomorrow.'
                  ],
                  [
                    'Question',
                    'Will + subject + <code>be</code> + <code>verb-ing</code>?',
                    '<code>Will</code> you <code>be working</code> late?'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>will be waiting</code> for you at the station.',
              'At 9 PM, they <code>will be having</code> dinner.',
              '<code>Will</code> she <code>be using</code> the computer tomorrow?',
              'He <code>will not be attending</code> the meeting.'
            ]
          },
          error: null
        },
        {
          name: 'Future Perfect',
          slug: 'future-perfect',
          rule: {
            slug: 'future-perfect',
            title: 'Future Perfect',
            description:
              'The Future Perfect tense is used to talk about actions that will be completed before a certain point in the future. It is formed using <code>will have</code> followed by the past participle of the verb. This tense helps to show the completion of an action in the future.',
            tables: [
              {
                caption: 'Forming the Future Perfect',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Affirmative',
                    '<code>will have</code> + past participle',
                    'She <code>will have finished</code> her homework.'
                  ],
                  [
                    'Negative',
                    '<code>will not have</code> + past participle',
                    'He <code>will not have arrived</code> by noon.'
                  ],
                  [
                    'Question',
                    '<code>Will</code> + subject + <code>have</code> + past participle?',
                    '<code>Will</code> you <code>have completed</code> the task?'
                  ]
                ]
              }
            ],
            examples: [
              'By next year, I <code>will have graduated</code> from university.',
              'They <code>will have traveled</code> to five countries by the end of summer.',
              '<code>Will</code> she <code>have finished</code> the project by Friday?',
              'He <code>will not have left</code> the office by 5 PM.'
            ]
          },
          error: null
        },
        {
          name: 'Future Simple',
          slug: 'future-simple',
          rule: {
            slug: 'future-simple',
            title: 'Future Simple',
            description:
              'The future simple tense is used to talk about actions that will happen in the future. Use <code>will</code> plus the base form of the verb to make this tense. It is often used for decisions made at the moment of speaking.',
            tables: [
              {
                caption: 'Forming the Future Simple',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Affirmative',
                    '<code>will</code> + base verb',
                    'I <code>will go</code> to the store.'
                  ],
                  [
                    'Negative',
                    '<code>will not</code> + base verb',
                    'She <code>will not come</code> to the party.'
                  ],
                  [
                    'Question',
                    '<code>Will</code> + subject + base verb?',
                    '<code>Will</code> you <code>help</code> me?'
                  ]
                ]
              }
            ],
            examples: [
              'They <code>will travel</code> to Spain next year.',
              'He <code>will not eat</code> dinner tonight.',
              '<code>Will</code> she <code>call</code> you tomorrow?'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Irregular',
      slug: 'irregular',
      topics: [
        {
          name: 'Past Participle Irregular',
          slug: 'past-participle-irregular',
          rule: {
            slug: 'past-participle-irregular',
            title: 'Past Participle: Irregular Verbs',
            description:
              "Irregular verbs do not follow a fixed pattern when forming the past participle. These verbs must be memorized as they don't end in <code>-ed</code> like regular verbs.",
            tables: [
              {
                caption: 'Common Irregular Verbs',
                columns: ['Base Form', 'Past Simple', 'Past Participle'],
                rows: [
                  ['go', 'went', 'gone'],
                  ['see', 'saw', 'seen'],
                  ['take', 'took', 'taken'],
                  ['write', 'wrote', 'written'],
                  ['come', 'came', 'come']
                ]
              }
            ],
            examples: [
              'She has <code>gone</code> to the store.',
              'I have <code>seen</code> that movie before.',
              'They have <code>taken</code> their exams.',
              'He has <code>written</code> a letter.',
              'We have <code>come</code> to a decision.'
            ]
          },
          error: null
        },
        {
          name: 'Past Simple Irregular',
          slug: 'past-simple-irregular',
          rule: {
            slug: 'past-simple-irregular',
            title: 'Past Simple Irregular Verbs',
            description:
              'Irregular verbs have unique past forms that do not follow a fixed pattern. You must memorize these forms because they do not follow the regular <code>-ed</code> ending rule.',
            tables: [
              {
                caption: 'Common Irregular Verbs',
                columns: ['Base Form', 'Past Simple', 'Example'],
                rows: [
                  [
                    'go',
                    'went',
                    'Yesterday, I <code>went</code> to the store.'
                  ],
                  [
                    'have',
                    'had',
                    'She <code>had</code> a great time at the party.'
                  ],
                  [
                    'make',
                    'made',
                    'He <code>made</code> a cake for her birthday.'
                  ],
                  ['see', 'saw', 'We <code>saw</code> a movie last night.']
                ]
              }
            ],
            examples: [
              'He <code>took</code> the last train home.',
              'We <code>ate</code> dinner at 7 PM.',
              'They <code>bought</code> a new car last month.',
              'I <code>felt</code> happy when I heard the news.'
            ]
          },
          error: null
        },
        {
          name: 'Past Simple Regular',
          slug: 'past-simple-regular',
          rule: {
            slug: 'past-simple-regular',
            title: 'Past Simple Regular Verbs',
            description:
              'The past simple tense is used to talk about actions that happened in the past. For regular verbs, add <code>-ed</code> to the base form. This tense is simple and common in everyday conversation.',
            tables: [
              {
                caption: 'Forming the past simple with regular verbs',
                columns: ['Base Form', 'Past Simple', 'Example'],
                rows: [
                  ['walk', 'walked', 'I <code>walked</code> to school.'],
                  ['play', 'played', 'She <code>played</code> the piano.'],
                  ['watch', 'watched', 'We <code>watched</code> a movie.']
                ]
              },
              {
                caption: 'Spelling changes in regular verbs',
                columns: ['Rule', 'Change', 'Example'],
                rows: [
                  [
                    'Verbs ending in <code>-e</code>',
                    'Add <code>-d</code>',
                    'close → <code>closed</code>'
                  ],
                  [
                    'Verbs ending in consonant + <code>-y</code>',
                    'Change <code>-y</code> to <code>-ied</code>',
                    'try → <code>tried</code>'
                  ],
                  [
                    'Short vowel + consonant',
                    'Double consonant, add <code>-ed</code>',
                    'stop → <code>stopped</code>'
                  ]
                ]
              }
            ],
            examples: [
              'He <code>visited</code> his grandparents last week.',
              'They <code>started</code> a new project.',
              'We <code>listened</code> to music all day.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Modal',
      slug: 'modal',
      topics: [
        {
          name: 'Can Must Should',
          slug: 'can-must-should',
          rule: {
            slug: 'can-must-should',
            title: 'Can, Must, Should',
            description:
              'These are modal verbs used to express ability, obligation, and advice. Use <code>can</code> to talk about ability, <code>must</code> for strong obligation or necessity, and <code>should</code> for giving advice or recommendations.',
            tables: [
              {
                caption: 'Usage of Can, Must, and Should',
                columns: ['Modal Verb', 'Usage', 'Example'],
                rows: [
                  [
                    'can',
                    'Ability or possibility',
                    'She <code>can</code> swim very fast.'
                  ],
                  [
                    'must',
                    'Strong obligation',
                    'You <code>must</code> wear a seatbelt.'
                  ],
                  [
                    'should',
                    'Advice or recommendation',
                    'You <code>should</code> try the new restaurant.'
                  ]
                ]
              }
            ],
            examples: [
              'He <code>can</code> play the piano.',
              'We <code>must</code> finish the project by tomorrow.',
              'You <code>should</code> speak to your teacher about it.'
            ]
          },
          error: null
        },
        {
          name: 'Can vs Could',
          slug: 'can-vs-could',
          rule: {
            slug: 'can-vs-could',
            title: 'Can vs Could',
            description:
              'We use <code>can</code> to talk about ability in the present and <code>could</code> for ability in the past. <code>Could</code> is also used for polite requests or possibilities.',
            tables: [
              {
                caption: 'Using Can and Could',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  ['can', 'Present ability', 'I <code>can</code> swim.'],
                  [
                    'could',
                    'Past ability',
                    'She <code>could</code> sing well as a child.'
                  ],
                  [
                    'could',
                    'Polite request',
                    '<code>Could</code> you help me, please?'
                  ],
                  [
                    'could',
                    'Possibility',
                    'It <code>could</code> rain tomorrow.'
                  ]
                ]
              }
            ],
            examples: [
              'He <code>can</code> play the guitar.',
              'They <code>could</code> run fast when they were young.',
              '<code>Could</code> I borrow your pen?',
              'We <code>could</code> go to the park if it stops raining.'
            ]
          },
          error: null
        },
        {
          name: 'Dare',
          slug: 'dare',
          rule: {
            slug: 'dare',
            title: 'Dare',
            description:
              'The verb <code>dare</code> can be used as a main verb or a modal verb. As a modal verb, it is often used in negative or interrogative sentences, especially in British English.',
            tables: [
              {
                caption: "Forms of 'Dare'",
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  [
                    'dare',
                    'Main verb with <code>to</code>',
                    'He <code>dares to</code> try new things.'
                  ],
                  [
                    'dare not',
                    'Modal, negative',
                    'She <code>dare not</code> speak.'
                  ],
                  [
                    'dare',
                    'Modal, question',
                    '<code>Dare</code> he swim there?'
                  ]
                ]
              }
            ],
            examples: [
              'He <code>dares to</code> dream big.',
              'I <code>dare not</code> go alone.',
              '<code>Dare</code> you ask him?',
              'She <code>dares</code> to challenge the rules.'
            ]
          },
          error: null
        },
        {
          name: 'Have To',
          slug: 'have-to',
          rule: {
            slug: 'have-to',
            title: 'Have To',
            description:
              "We use <code>have to</code> to talk about obligations or things that are necessary. It is used with the base form of the verb. The negative form is <code>don't have to</code> to show that something is not necessary.",
            tables: [
              {
                caption: "Forms of 'Have To'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'have to',
                    'Used for necessity or obligation',
                    'I <code>have to</code> study for my exam.'
                  ],
                  [
                    "don't have to",
                    'Used when something is not necessary',
                    "You <code>don't have to</code> come if you're busy."
                  ],
                  [
                    'has to',
                    'Used with he/she/it for necessity',
                    'She <code>has to</code> finish her homework.'
                  ]
                ]
              }
            ],
            examples: [
              'We <code>have to</code> wear uniforms at school.',
              "He <code>doesn't have to</code> work on weekends.",
              'They <code>have to</code> leave early tomorrow.'
            ]
          },
          error: null
        },
        {
          name: 'May Might',
          slug: 'may-might',
          rule: {
            slug: 'may-might',
            title: 'May and Might',
            description:
              'We use <code>may</code> and <code>might</code> to talk about possible events or actions. <code>May</code> suggests a higher possibility, while <code>might</code> suggests a lower one. Both can be used for permissions or polite requests.',
            tables: [
              {
                caption: 'Usage of May and Might',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'may',
                    'Higher possibility',
                    'It <code>may</code> rain today.'
                  ],
                  [
                    'might',
                    'Lower possibility',
                    'I <code>might</code> go to the party.'
                  ],
                  ['may', 'Permission', '<code>May</code> I leave early?'],
                  [
                    'might',
                    'Past possibility',
                    'He <code>might</code> have missed the bus.'
                  ]
                ]
              }
            ],
            examples: [
              'She <code>may</code> arrive late due to traffic.',
              'They <code>might</code> not come to the meeting.',
              '<code>May</code> I use your phone?',
              'He <code>might</code> have forgotten to call.'
            ]
          },
          error: null
        },
        {
          name: 'Need',
          slug: 'need',
          rule: {
            slug: 'need',
            title: 'Need',
            description:
              'The word <code>need</code> can be a verb or a noun. As a verb, it shows necessity, like in <code>I need water</code>. As a noun, it refers to something necessary, like in <code>basic needs</code>.',
            tables: [
              {
                caption: "Using 'need' as a verb",
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  ['need', 'Present tense', 'I <code>need</code> help.'],
                  [
                    'needed',
                    'Past tense',
                    'She <code>needed</code> a pen yesterday.'
                  ],
                  [
                    'will need',
                    'Future tense',
                    'We <code>will need</code> more time.'
                  ]
                ]
              },
              {
                caption: "Using 'need' as a noun",
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  ['need', 'Singular', 'This is a basic <code>need</code>.'],
                  [
                    'needs',
                    'Plural',
                    'Everyone has different <code>needs</code>.'
                  ],
                  [
                    'in need',
                    'Expression',
                    'They are <code>in need</code> of food.'
                  ]
                ]
              }
            ],
            examples: [
              'You <code>need</code> to study for the test.',
              'He <code>needed</code> a new phone.',
              'They <code>will need</code> to leave soon.',
              'Water is a basic <code>need</code>.',
              'Her <code>needs</code> are simple.',
              'The charity helps those <code>in need</code>.'
            ]
          },
          error: null
        },
        {
          name: 'Ought To',
          slug: 'ought-to',
          rule: {
            slug: 'ought-to',
            title: 'Ought To',
            description:
              'We use <code>ought to</code> to give advice or express a moral obligation. It is similar to <code>should</code> but is often considered stronger. <code>Ought to</code> is always followed by the base form of the verb.',
            tables: [
              {
                caption: 'Usage of Ought To',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Ought to + base verb',
                    'Advice or suggestion',
                    'You <code>ought to</code> study more.'
                  ],
                  [
                    'Ought not to + base verb',
                    'Negative form',
                    'He <code>ought not to</code> go there.'
                  ],
                  [
                    'Ought to + have + past participle',
                    'Past advice or regret',
                    'She <code>ought to have</code> called you.'
                  ]
                ]
              }
            ],
            examples: [
              'You <code>ought to</code> apologize.',
              'They <code>ought not to</code> be so loud.',
              'We <code>ought to have</code> left earlier.'
            ]
          },
          error: null
        },
        {
          name: 'Shall',
          slug: 'shall',
          rule: {
            slug: 'shall',
            title: 'Shall',
            description:
              'The word <code>shall</code> is used to express future actions, promises, or suggestions, mainly in formal contexts. It is often used with <code>I</code> or <code>we</code> to talk about something planned or intended.',
            tables: [
              {
                caption: 'Usage of Shall',
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  [
                    'shall',
                    'Future action with I/we',
                    '<code>Shall</code> we go to the park?'
                  ],
                  [
                    'shall',
                    'Formal promises',
                    '<code>Shall</code> I help you with that?'
                  ],
                  [
                    'shall',
                    'Suggestions',
                    '<code>Shall</code> we start the meeting?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Shall</code> I open the window?',
              '<code>Shall</code> we have dinner together?',
              'You <code>shall</code> receive your package tomorrow.'
            ]
          },
          error: null
        },
        {
          name: 'Would vs Used To',
          slug: 'would-vs-used-to',
          rule: {
            slug: 'would-vs-used-to',
            title: 'Would vs Used To',
            description:
              'Both <code>would</code> and <code>used to</code> describe past habits or repeated actions. Use <code>would</code> when focusing on actions, and <code>used to</code> for both actions and states.',
            tables: [
              {
                caption: 'Usage of Would vs Used To',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'would',
                    'Repeated actions in the past',
                    'Every summer, we <code>would</code> go to the beach.'
                  ],
                  [
                    'used to',
                    'Past habits or states',
                    'I <code>used to</code> have long hair.'
                  ],
                  [
                    'used to',
                    'Both actions and states',
                    'They <code>used to</code> live in New York.'
                  ]
                ]
              }
            ],
            examples: [
              'When I was a child, I <code>would</code> play outside every day.',
              'She <code>used to</code> work here.',
              'We <code>would</code> visit our grandparents every weekend.',
              'He <code>used to</code> be a teacher.'
            ]
          },
          error: null
        },
        {
          name: 'Would',
          slug: 'would',
          rule: {
            slug: 'would',
            title: 'Would',
            description:
              "The word <code>would</code> is a modal verb used to talk about future possibilities, polite requests, or imagined situations. It's often used in conditional sentences and to express past habits or preferences.",
            tables: [
              {
                caption: 'Uses of would',
                columns: ['Use', 'Description', 'Example'],
                rows: [
                  [
                    'Future possibility',
                    'Talking about what might happen',
                    'I <code>would</code> go if I had time.'
                  ],
                  [
                    'Polite request',
                    'Asking for something politely',
                    '<code>Would</code> you help me, please?'
                  ],
                  [
                    'Imagined situation',
                    'Describing a hypothetical scenario',
                    'If I were rich, I <code>would</code> travel the world.'
                  ],
                  [
                    'Past habit',
                    'Describing actions done regularly in the past',
                    'When I was a child, I <code>would</code> play outside every day.'
                  ]
                ]
              }
            ],
            examples: [
              'He <code>would</code> like some more coffee.',
              '<code>Would</code> you mind closing the door?',
              'If I had a car, I <code>would</code> drive to the beach.',
              'Every summer, we <code>would</code> visit our grandparents.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Multiple Choices',
      slug: 'multiple-choices',
      topics: [
        {
          name: 'Adverbs of Frequency',
          slug: 'adverbs-of-frequency',
          rule: {
            slug: 'adverbs-of-frequency',
            title: 'Adverbs of Frequency',
            description:
              'Adverbs of frequency tell us how often something happens. They usually come before the main verb but after the verb <code>to be</code>. Common examples include <code>always</code>, <code>usually</code>, and <code>sometimes</code>.',
            tables: [
              {
                caption: 'Position of adverbs of frequency',
                columns: ['Adverb', 'Position', 'Example'],
                rows: [
                  [
                    'always',
                    'Before main verb',
                    'She <code>always</code> eats breakfast.'
                  ],
                  [
                    'usually',
                    'After <code>to be</code>',
                    'He is <code>usually</code> on time.'
                  ],
                  [
                    'sometimes',
                    'Before main verb',
                    'They <code>sometimes</code> walk to work.'
                  ],
                  [
                    'never',
                    'Before main verb',
                    'I <code>never</code> eat meat.'
                  ]
                ]
              }
            ],
            examples: [
              'She <code>often</code> goes to the gym.',
              'He is <code>always</code> happy.',
              'They <code>rarely</code> watch TV.',
              'We <code>sometimes</code> travel by train.'
            ]
          },
          error: null
        },
        {
          name: 'As Like',
          slug: 'as-like',
          rule: {
            slug: 'as-like',
            title: 'As vs. Like',
            description:
              "Use <code>as</code> to mean 'in the role of' or to compare actions. Use <code>like</code> to compare similarities between things or people. Both are used to make comparisons but in different contexts.",
            tables: [
              {
                caption: "Using 'as' and 'like'",
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'as',
                    'In the role of',
                    'She works <code>as</code> a teacher.'
                  ],
                  [
                    'as',
                    'Comparing actions',
                    'Do it <code>as</code> I told you.'
                  ],
                  [
                    'like',
                    'Comparing things',
                    'He sings <code>like</code> a professional.'
                  ],
                  [
                    'like',
                    'Similar appearance',
                    'This tastes <code>like</code> chocolate.'
                  ]
                ]
              }
            ],
            examples: [
              'He acts <code>as</code> if nothing happened.',
              'She looks <code>like</code> her mother.',
              'Speak <code>as</code> clearly as you can.',
              'He plays the piano <code>like</code> a master.'
            ]
          },
          error: null
        },
        {
          name: 'Both Either Neither',
          slug: 'both-either-neither',
          rule: {
            slug: 'both-either-neither',
            title: 'Both, Either, Neither',
            description:
              'Use <code>both</code> to talk about two things together. Use <code>either</code> to talk about one of two options. Use <code>neither</code> when not choosing either option.',
            tables: [
              {
                caption: 'Usage of both',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'both',
                    'Used with plural nouns',
                    'I like <code>both</code> cats and dogs.'
                  ],
                  [
                    'both of',
                    'Before pronouns',
                    '<code>Both of</code> them are nice.'
                  ]
                ]
              },
              {
                caption: 'Usage of either',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'either',
                    'One of two choices',
                    'You can have <code>either</code> tea or coffee.'
                  ],
                  [
                    'either of',
                    'Before pronouns',
                    '<code>Either of</code> the options is fine.'
                  ]
                ]
              },
              {
                caption: 'Usage of neither',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'neither',
                    'Not one or the other',
                    '<code>Neither</code> answer is correct.'
                  ],
                  [
                    'neither of',
                    'Before pronouns',
                    '<code>Neither of</code> them is ready.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Both</code> my parents are teachers.',
              'Would you like <code>either</code> the red or blue shirt?',
              '<code>Neither</code> of them wants to go.',
              '<code>Both</code> apples are fresh.',
              "I don't like <code>either</code> option."
            ]
          },
          error: null
        },
        {
          name: 'Coordinating Conjunctions',
          slug: 'coordinating-conjunctions',
          rule: {
            slug: 'coordinating-conjunctions',
            title: 'Coordinating Conjunctions',
            description:
              'Coordinating conjunctions connect words, phrases, or clauses that are similar or equal. The most common are <code>for</code>, <code>and</code>, <code>nor</code>, <code>but</code>, <code>or</code>, <code>yet</code>, and <code>so</code>. Remember the acronym FANBOYS to help you remember them.',
            tables: [
              {
                caption: 'Common coordinating conjunctions and their uses',
                columns: ['Conjunction', 'Usage', 'Example'],
                rows: [
                  [
                    'for',
                    'Reason or cause',
                    'I stayed home, <code>for</code> it was raining.'
                  ],
                  ['and', 'Addition', 'She likes tea <code>and</code> coffee.'],
                  [
                    'nor',
                    'Negative addition',
                    "He didn't call <code>nor</code> did he write."
                  ],
                  ['but', 'Contrast', 'She is small, <code>but</code> strong.'],
                  ['or', 'Choice', 'Do you want tea <code>or</code> coffee?'],
                  [
                    'yet',
                    'Unexpected outcome',
                    "It was late, <code>yet</code> she wasn't tired."
                  ],
                  [
                    'so',
                    'Result',
                    'It was raining, <code>so</code> I stayed home.'
                  ]
                ]
              }
            ],
            examples: [
              'She is tired <code>but</code> happy.',
              'We can go to the beach <code>or</code> the park.',
              'He was hungry, <code>so</code> he ate dinner.'
            ]
          },
          error: null
        },
        {
          name: 'Ever Never',
          slug: 'ever-never',
          rule: {
            slug: 'ever-never',
            title: 'Ever and Never',
            description:
              'Use <code>ever</code> in questions to ask if something has happened at any time. Use <code>never</code> to say that something has not happened at any time. These words often come with the present perfect tense.',
            tables: [
              {
                caption: 'Usage of Ever and Never',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'ever',
                    'In questions',
                    'Have you <code>ever</code> been to France?'
                  ],
                  [
                    'never',
                    'In negative statements',
                    'I have <code>never</code> tried sushi.'
                  ]
                ]
              }
            ],
            examples: [
              'Have you <code>ever</code> seen a dolphin?',
              'She has <code>never</code> missed a class.',
              'Have they <code>ever</code> traveled abroad?',
              'He has <code>never</code> eaten spicy food.'
            ]
          },
          error: null
        },
        {
          name: 'For Since Ago Until During',
          slug: 'for-since-ago-until-during',
          rule: {
            slug: 'for-since-ago-until-during',
            title: 'For, Since, Ago, Until, During',
            description:
              'These words help us talk about time. Use <code>for</code> with a period, <code>since</code> for a starting point, <code>ago</code> for past time from now, <code>until</code> for an end point, and <code>during</code> for something happening in a time frame.',
            tables: [
              {
                caption: 'How to use each time word',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  ['for', 'Period of time', '<code>for</code> two hours'],
                  ['since', 'Starting point', '<code>since</code> last Monday'],
                  ['ago', 'Past time from now', 'two days <code>ago</code>'],
                  [
                    'until',
                    'End point in time',
                    'wait <code>until</code> 5 PM'
                  ],
                  [
                    'during',
                    'Within a time frame',
                    '<code>during</code> the meeting'
                  ]
                ]
              }
            ],
            examples: [
              'I have lived here <code>for</code> three years.',
              'She has been working <code>since</code> 2010.',
              'He left the house two hours <code>ago</code>.',
              'We will stay here <code>until</code> Sunday.',
              'I read a book <code>during</code> the flight.'
            ]
          },
          error: null
        },
        {
          name: 'How Much How Many',
          slug: 'how-much-how-many',
          rule: {
            slug: 'how-much-how-many',
            title: 'How Much / How Many',
            description:
              'We use <code>how much</code> for uncountable nouns and <code>how many</code> for countable nouns. These questions help us ask about the quantity or amount of something.',
            tables: [
              {
                caption: 'Using How Much and How Many',
                columns: ['Question Word', 'Usage', 'Example'],
                rows: [
                  [
                    'how much',
                    'Uncountable nouns',
                    'How much <code>water</code> is there?'
                  ],
                  [
                    'how much',
                    'Uncountable nouns',
                    'How much <code>money</code> do you have?'
                  ],
                  [
                    'how many',
                    'Countable nouns',
                    'How many <code>apples</code> are there?'
                  ],
                  [
                    'how many',
                    'Countable nouns',
                    'How many <code>cars</code> do you see?'
                  ]
                ]
              }
            ],
            examples: [
              'How much <code>rice</code> do we need?',
              'How many <code>books</code> are on the shelf?',
              'Can you tell me how much <code>milk</code> is left?',
              'Do you know how many <code>students</code> are in the class?'
            ]
          },
          error: null
        },
        {
          name: 'Just Already Yet',
          slug: 'just-already-yet',
          rule: {
            slug: 'just-already-yet',
            title: 'Just, Already, Yet',
            description:
              'Use <code>just</code>, <code>already</code>, and <code>yet</code> with the present perfect tense to talk about recent actions or expectations. <code>Just</code> is for very recent actions, <code>already</code> for actions done sooner than expected, and <code>yet</code> for actions that are expected but not done.',
            tables: [
              {
                caption: 'Usage of Just, Already, and Yet',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'just',
                    'Very recent action',
                    'I have <code>just</code> finished my homework.'
                  ],
                  [
                    'already',
                    'Action done earlier than expected',
                    'She has <code>already</code> eaten lunch.'
                  ],
                  [
                    'yet',
                    'Expected action not done (in negatives/questions)',
                    'Have you finished your work <code>yet</code>?'
                  ]
                ]
              }
            ],
            examples: [
              'They have <code>just</code> left the house.',
              'We have <code>already</code> seen that movie.',
              "I haven't called him <code>yet</code>."
            ]
          },
          error: null
        },
        {
          name: 'Present Perfect With For Since',
          slug: 'present-perfect-with-for-since',
          rule: {
            slug: 'present-perfect-with-for-since',
            title: 'Present Perfect with For and Since',
            description:
              'Use the present perfect tense to talk about actions or situations that started in the past and continue to the present. Use <code>for</code> to talk about the duration, and <code>since</code> to talk about the starting point.',
            tables: [
              {
                caption: 'Using For and Since',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  ['for', 'Period of time', '<code>for</code> two hours'],
                  ['for', 'Period of time', '<code>for</code> a week'],
                  [
                    'since',
                    'Specific point in time',
                    '<code>since</code> Monday'
                  ],
                  ['since', 'Specific point in time', '<code>since</code> 2015']
                ]
              }
            ],
            examples: [
              'I have lived here <code>for</code> three years.',
              'She has worked there <code>since</code> last summer.',
              'They have known each other <code>for</code> a long time.',
              'He has been my friend <code>since</code> childhood.'
            ]
          },
          error: null
        },
        {
          name: 'Relative Clauses',
          slug: 'relative-clauses',
          rule: {
            slug: 'relative-clauses',
            title: 'Relative Clauses',
            description:
              'Relative clauses give more information about a noun. They often start with words like <code>who</code>, <code>which</code>, or <code>that</code>. Use them to connect ideas and add detail.',
            tables: [
              {
                caption: 'Common relative pronouns',
                columns: ['Pronoun', 'Usage', 'Example'],
                rows: [
                  [
                    'who',
                    'For people',
                    'The teacher <code>who</code> helped me'
                  ],
                  ['which', 'For things', 'The book <code>which</code> I read'],
                  [
                    'that',
                    'For people or things',
                    'The cat <code>that</code> is sleeping'
                  ]
                ]
              }
            ],
            examples: [
              'The girl <code>who</code> lives next door is friendly.',
              'I like the car <code>that</code> you bought.',
              'This is the movie <code>which</code> won an award.'
            ]
          },
          error: null
        },
        {
          name: 'So Neither',
          slug: 'so-neither',
          rule: {
            slug: 'so-neither',
            title: 'So / Neither',
            description:
              'We use <code>so</code> and <code>neither</code> to show agreement in English. Use <code>so</code> to agree with positive statements and <code>neither</code> with negative ones. They help us avoid repeating the full sentence.',
            tables: [
              {
                caption: 'How to use so and neither',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'so',
                    'Agree with positive statement',
                    'I like coffee. <code>So</code> do I.'
                  ],
                  [
                    'neither',
                    'Agree with negative statement',
                    "I don't like tea. <code>Neither</code> do I."
                  ],
                  [
                    'so',
                    'Used with auxiliary verb',
                    'She can swim. <code>So</code> can he.'
                  ],
                  [
                    'neither',
                    'Used with auxiliary verb',
                    "They haven't finished. <code>Neither</code> have we."
                  ]
                ]
              }
            ],
            examples: [
              'He is tired. <code>So</code> am I.',
              "She doesn't eat meat. <code>Neither</code> do I.",
              'They will join us. <code>So</code> will we.',
              "I can't swim. <code>Neither</code> can she."
            ]
          },
          error: null
        },
        {
          name: 'Too Either',
          slug: 'too-either',
          rule: {
            slug: 'too-either',
            title: 'Too and Either',
            description:
              'We use <code>too</code> to agree with positive statements, and <code>either</code> to agree with negative ones. They come at the end of a sentence.',
            tables: [
              {
                caption: 'Using too and either',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'too',
                    'Agree with positive',
                    'I like coffee. <code>Me too</code>.'
                  ],
                  [
                    'either',
                    'Agree with negative',
                    "I don't like tea. <code>Me either</code>."
                  ]
                ]
              }
            ],
            examples: [
              'I love pizza. <code>Me too</code>.',
              "She doesn't enjoy running. <code>Me either</code>.",
              'He is going to the party. <code>Me too</code>.',
              "They aren't coming. <code>Me either</code>."
            ]
          },
          error: null
        },
        {
          name: 'Too Enough',
          slug: 'too-enough',
          rule: {
            slug: 'too-enough',
            title: 'Too and Enough',
            description:
              'Use <code>too</code> to express something excessive or more than needed, and <code>enough</code> to show sufficient amount or degree. The word <code>too</code> comes before adjectives or adverbs, while <code>enough</code> follows them.',
            tables: [
              {
                caption: 'Using Too and Enough',
                columns: ['Word', 'Position', 'Example'],
                rows: [
                  [
                    'too',
                    'Before adjective/adverb',
                    "It's <code>too</code> hot."
                  ],
                  [
                    'enough',
                    'After adjective/adverb',
                    'She is tall <code>enough</code>.'
                  ],
                  ['enough', 'Before noun', 'We have <code>enough</code> time.']
                ]
              }
            ],
            examples: [
              'The coffee is <code>too</code> strong for me.',
              'He runs <code>too</code> slowly to win.',
              'She is not old <code>enough</code> to drive.',
              'Do we have <code>enough</code> chairs for everyone?'
            ]
          },
          error: null
        },
        {
          name: 'Who Whom',
          slug: 'who-whom',
          rule: {
            slug: 'who-whom',
            title: 'Who vs. Whom',
            description:
              'Use <code>who</code> as the subject of a sentence or question. Use <code>whom</code> as the object of a verb or preposition. This helps to know who is doing the action and to whom it is done.',
            tables: [
              {
                caption: 'Using Who and Whom',
                columns: ['Word', 'Function', 'Example'],
                rows: [
                  [
                    'who',
                    'Subject of the sentence',
                    '<code>Who</code> is calling?'
                  ],
                  [
                    'whom',
                    'Object of a verb',
                    'To <code>whom</code> did you speak?'
                  ],
                  [
                    'whom',
                    'Object of a preposition',
                    'With <code>whom</code> are you going?'
                  ]
                ]
              }
            ],
            examples: [
              'I met the person <code>who</code> won the prize.',
              '<code>Whom</code> did you invite to the party?',
              'He is someone <code>whom</code> I trust.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Negative Form',
      slug: 'negative-form',
      topics: [
        {
          name: 'Be Going To Negative',
          slug: 'be-going-to-negative',
          rule: {
            slug: 'be-going-to-negative',
            title: 'Be Going To Negative',
            description:
              'Use <code>be going to</code> for talking about future plans that will not happen. Add <code>not</code> after the verb <code>be</code> to make it negative. This shows that something is not planned or expected.',
            tables: [
              {
                caption: "Forming the negative with 'be going to'",
                columns: ['Subject', 'Be + not', 'Example'],
                rows: [
                  [
                    'I',
                    'am not going to',
                    'I <code>am not going to</code> eat that.'
                  ],
                  [
                    'He/She/It',
                    'is not going to',
                    'She <code>is not going to</code> attend the meeting.'
                  ],
                  [
                    'We/You/They',
                    'are not going to',
                    'They <code>are not going to</code> watch the movie.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>am not going to</code> visit him.',
              'They <code>are not going to</code> finish the project.',
              'He <code>is not going to</code> play soccer tomorrow.'
            ]
          },
          error: null
        },
        {
          name: 'Future Simple Negative',
          slug: 'future-simple-negative',
          rule: {
            slug: 'future-simple-negative',
            title: 'Future Simple Negative',
            description:
              "The future simple negative is used to say that something will not happen in the future. Use <code>will not</code> or <code>won't</code> before the main verb.",
            tables: [
              {
                caption: 'Forming Future Simple Negative',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'will not',
                    'Full form',
                    'I <code>will not</code> go to the party.'
                  ],
                  [
                    "won't",
                    'Contraction of will not',
                    "She <code>won't</code> eat breakfast."
                  ],
                  [
                    'will not + verb',
                    'Main verb stays in base form',
                    'They <code>will not</code> play soccer.'
                  ]
                ]
              }
            ],
            examples: [
              "He <code>won't</code> watch the movie.",
              'We <code>will not</code> travel next week.',
              "I <code>won't</code> finish my homework tonight.",
              'They <code>will not</code> join the meeting.'
            ]
          },
          error: null
        },
        {
          name: 'Past Continuous Negative',
          slug: 'past-continuous-negative',
          rule: {
            slug: 'past-continuous-negative',
            title: 'Past Continuous Negative',
            description:
              'The past continuous negative is used to describe actions that were not happening at a specific time in the past. It is formed by using <code>was not</code> or <code>were not</code> followed by the verb ending in <code>-ing</code>.',
            tables: [
              {
                caption: 'Forming the past continuous negative',
                columns: ['Subject', 'Negative Form', 'Example'],
                rows: [
                  ['I', 'was not', 'I <code>was not</code> working'],
                  ['You', 'were not', 'You <code>were not</code> sleeping'],
                  ['He/She/It', 'was not', 'He <code>was not</code> playing'],
                  ['We', 'were not', 'We <code>were not</code> watching TV'],
                  ['They', 'were not', 'They <code>were not</code> eating']
                ]
              }
            ],
            examples: [
              'He <code>was not</code> studying when I called.',
              'We <code>were not</code> eating dinner at 7 PM.',
              'They <code>were not</code> watching the movie.'
            ]
          },
          error: null
        },
        {
          name: 'Past Perfect Continuous Negative',
          slug: 'past-perfect-continuous-negative',
          rule: {
            slug: 'past-perfect-continuous-negative',
            title: 'Past Perfect Continuous Negative',
            description:
              'The past perfect continuous negative is used to talk about actions that were not happening before a certain point in the past. Form it with <code>had not been</code> followed by the <code>verb</code> ending in <code>-ing</code>. It shows the duration of the action that did not occur.',
            tables: [
              {
                caption: 'Forming the past perfect continuous negative',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'had not been + verb + -ing',
                    'Negative form',
                    'She <code>had not been studying</code> for long.'
                  ],
                  [
                    "hadn't been + verb + -ing",
                    'Contracted negative',
                    "They <code>hadn't been playing</code> outside."
                  ],
                  [
                    'had not been + verb + -ing',
                    'Negative with time reference',
                    'He <code>had not been working</code> there for two years.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>had not been waiting</code> long before the bus arrived.',
              "They <code>hadn't been living</code> in the city before they moved.",
              'She <code>had not been feeling</code> well for days.'
            ]
          },
          error: null
        },
        {
          name: 'Past Perfect Negative',
          slug: 'past-perfect-negative',
          rule: {
            slug: 'past-perfect-negative',
            title: 'Past Perfect Negative',
            description:
              "The past perfect negative is used to show that an action was not completed before another past action. It uses <code>had not</code> or <code>hadn't</code> followed by the past participle of the verb.",
            tables: [
              {
                caption: 'Forming the past perfect negative',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'I/You/He/She/We/They',
                    '<code>had not</code> + past participle',
                    'He <code>had not</code> finished.'
                  ],
                  [
                    'I/You/He/She/We/They',
                    "<code>hadn't</code> + past participle",
                    "They <code>hadn't</code> arrived."
                  ]
                ]
              }
            ],
            examples: [
              "She <code>hadn't</code> eaten before the meeting.",
              'We <code>had not</code> seen the movie when it was discussed.',
              "By the time he called, I <code>hadn't</code> finished my homework."
            ]
          },
          error: null
        },
        {
          name: 'Past Simple Negative',
          slug: 'past-simple-negative',
          rule: {
            slug: 'past-simple-negative',
            title: 'Past Simple Negative',
            description:
              "The past simple negative is used to talk about actions that did not happen in the past. We form it using <code>did not</code> or <code>didn't</code> followed by the base form of the verb.",
            tables: [
              {
                caption: 'Forming the Past Simple Negative',
                columns: ['Subject', 'Negative Form', 'Example'],
                rows: [
                  [
                    'I/You/He/She/It/We/They',
                    'did not + base verb',
                    'I <code>did not</code> go'
                  ],
                  [
                    'I/You/He/She/It/We/They',
                    "didn't + base verb",
                    "She <code>didn't</code> eat"
                  ],
                  [
                    'I/You/He/She/It/We/They',
                    'did not + base verb',
                    'They <code>did not</code> see'
                  ]
                ]
              }
            ],
            examples: [
              "He <code>didn't</code> play football yesterday.",
              'We <code>did not</code> watch the movie.',
              "They <code>didn't</code> understand the question."
            ]
          },
          error: null
        },
        {
          name: 'Present Continuous Negative',
          slug: 'present-continuous-negative',
          rule: {
            slug: 'present-continuous-negative',
            title: 'Present Continuous Negative',
            description:
              "The present continuous negative is used to describe actions that are not happening right now. Form it with <code>is not</code> or <code>are not</code> followed by a verb ending in <code>-ing</code>. It's used to talk about actions not taking place at the moment of speaking.",
            tables: [
              {
                caption: 'Forming the present continuous negative',
                columns: ['Subject', 'Negative Form', 'Example'],
                rows: [
                  [
                    'I',
                    'am not + verb + -ing',
                    'I <code>am not</code> studying.'
                  ],
                  [
                    'He/She/It',
                    'is not + verb + -ing',
                    'She <code>is not</code> working.'
                  ],
                  [
                    'We/You/They',
                    'are not + verb + -ing',
                    'They <code>are not</code> playing.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>am not</code> eating lunch right now.',
              'He <code>is not</code> watching TV.',
              'We <code>are not</code> going to the party.'
            ]
          },
          error: null
        },
        {
          name: 'Present Perfect Negative',
          slug: 'present-perfect-negative',
          rule: {
            slug: 'present-perfect-negative',
            title: 'Present Perfect Negative',
            description:
              "The present perfect negative is used to talk about actions that have not happened yet or to emphasize the absence of an experience. Form it with <code>have not</code> or <code>has not</code> plus the past participle of the verb. Use <code>haven't</code> or <code>hasn't</code> for contractions.",
            tables: [
              {
                caption: 'Forming the present perfect negative',
                columns: ['Subject', 'Negative Form', 'Past Participle'],
                rows: [
                  ['I/You/We/They', "have not (haven't)", 'seen'],
                  ['He/She/It', "has not (hasn't)", 'eaten'],
                  ['I/You/We/They', "have not (haven't)", 'finished'],
                  ['He/She/It', "has not (hasn't)", 'gone']
                ]
              }
            ],
            examples: [
              "I <code>haven't</code> finished my homework yet.",
              "She <code>hasn't</code> seen that movie.",
              "We <code>haven't</code> visited Paris.",
              "He <code>hasn't</code> called his friend."
            ]
          },
          error: null
        },
        {
          name: 'Present Simple Negative',
          slug: 'present-simple-negative',
          rule: {
            slug: 'present-simple-negative',
            title: 'Present Simple Negative',
            description:
              'The present simple negative is used to describe actions that do not happen regularly or facts that are not true. Use <code>do not</code> or <code>does not</code> before the base form of the verb, with <code>does not</code> for third person singular.',
            tables: [
              {
                caption: 'Forming the Present Simple Negative',
                columns: ['Subject', 'Negative Form', 'Example'],
                rows: [
                  [
                    'I/You/We/They',
                    '<code>do not</code> + base verb',
                    'I <code>do not</code> like coffee.'
                  ],
                  [
                    'He/She/It',
                    '<code>does not</code> + base verb',
                    'She <code>does not</code> play tennis.'
                  ]
                ]
              }
            ],
            examples: [
              'They <code>do not</code> work on Sundays.',
              'He <code>does not</code> eat meat.',
              'We <code>do not</code> have school tomorrow.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Noun',
      slug: 'noun',
      topics: [
        {
          name: 'A An Some Any',
          slug: 'a-an-some-any',
          rule: {
            slug: 'a-an-some-any',
            title: 'A, An, Some, Any',
            description:
              'Use <code>a</code> and <code>an</code> with singular countable nouns. <code>Some</code> and <code>any</code> are for plural countable nouns or uncountable nouns. <code>Some</code> is often used in positive sentences, while <code>any</code> is used in negatives or questions.',
            tables: [
              {
                caption: 'Using A and An',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  ['a', 'Before consonant sound', '<code>a</code> car'],
                  ['an', 'Before vowel sound', '<code>an</code> orange']
                ]
              },
              {
                caption: 'Using Some and Any',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  ['some', 'Positive sentences', '<code>some</code> water'],
                  [
                    'any',
                    'Negative sentences',
                    'I don’t have <code>any</code> money'
                  ],
                  ['any', 'Questions', 'Do you have <code>any</code> pets?']
                ]
              }
            ],
            examples: [
              'I need <code>a</code> pen.',
              'He wants <code>an</code> egg.',
              'We have <code>some</code> apples.',
              'Do you have <code>any</code> questions?',
              'She doesn’t have <code>any</code> friends here.'
            ]
          },
          error: null
        },
        {
          name: 'Little Few',
          slug: 'little-few',
          rule: {
            slug: 'little-few',
            title: 'Little and Few',
            description:
              'Use <code>little</code> for uncountable nouns and <code>few</code> for countable nouns. These words mean there is not much or not many of something.',
            tables: [
              {
                caption: 'Usage of Little and Few',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'little',
                    'Uncountable nouns',
                    'There is <code>little</code> water in the glass.'
                  ],
                  [
                    'few',
                    'Countable nouns',
                    'She has <code>few</code> friends in the city.'
                  ],
                  [
                    'little',
                    'Uncountable nouns',
                    'He has <code>little</code> money left.'
                  ],
                  [
                    'few',
                    'Countable nouns',
                    'There are <code>few</code> books on the shelf.'
                  ]
                ]
              }
            ],
            examples: [
              'I have <code>little</code> time to spare.',
              'We saw <code>few</code> people at the party.',
              'She drank <code>little</code> milk this morning.',
              'There are <code>few</code> cars on the road today.'
            ]
          },
          error: null
        },
        {
          name: 'Many Much',
          slug: 'many-much',
          rule: {
            slug: 'many-much',
            title: 'Many vs. Much',
            description:
              'We use <code>many</code> with countable nouns and <code>much</code> with uncountable nouns. Countable nouns are things we can count, like <code>books</code>, and uncountable nouns are things we cannot easily count, like <code>water</code>.',
            tables: [
              {
                caption: 'Using Many and Much',
                columns: ['Word', 'Type of Noun', 'Example'],
                rows: [
                  [
                    'many',
                    'Countable nouns',
                    'I have <code>many</code> friends.'
                  ],
                  [
                    'much',
                    'Uncountable nouns',
                    "There isn't <code>much</code> milk left."
                  ],
                  [
                    'many',
                    'Countable nouns',
                    'How <code>many</code> apples are there?'
                  ],
                  [
                    'much',
                    'Uncountable nouns',
                    'How <code>much</code> sugar do you need?'
                  ]
                ]
              }
            ],
            examples: [
              'She has <code>many</code> books in her library.',
              "There isn't <code>much</code> time to waste.",
              'How <code>many</code> students are in your class?',
              'Is there <code>much</code> traffic today?'
            ]
          },
          error: null
        },
        {
          name: 'One vs Ones',
          slug: 'one-vs-ones',
          rule: {
            slug: 'one-vs-ones',
            title: 'One vs Ones',
            description:
              'Use <code>one</code> to refer back to a singular noun and <code>ones</code> for plural nouns. These words help avoid repeating the noun in a sentence.',
            tables: [
              {
                caption: "Using 'One' and 'Ones'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'one',
                    'Refers to a singular noun',
                    'I need a pen. Do you have <code>one</code>?'
                  ],
                  [
                    'ones',
                    'Refers to plural nouns',
                    'These apples are sweet. I like the green <code>ones</code>.'
                  ],
                  [
                    'one',
                    'Used with adjectives',
                    'I lost my red pen. Do you have a blue <code>one</code>?'
                  ]
                ]
              }
            ],
            examples: [
              'I want the small <code>one</code>, not the big one.',
              'These cookies are good. I like the chocolate <code>ones</code>.',
              'She has a red car. I want a blue <code>one</code>.'
            ]
          },
          error: null
        },
        {
          name: 'So Such',
          slug: 'so-such',
          rule: {
            slug: 'so-such',
            title: 'So and Such',
            description:
              'Use <code>so</code> to emphasize adjectives or adverbs, and <code>such</code> to emphasize nouns. Both words help express strong feelings or opinions.',
            tables: [
              {
                caption: "Using 'So'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'so + adjective',
                    'Emphasize an adjective',
                    'It is <code>so</code> cold today.'
                  ],
                  [
                    'so + adverb',
                    'Emphasize an adverb',
                    'She runs <code>so</code> quickly.'
                  ]
                ]
              },
              {
                caption: "Using 'Such'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'such + adjective + noun',
                    'Emphasize a noun with an adjective',
                    'He is <code>such</code> a nice person.'
                  ],
                  [
                    'such + noun',
                    'Emphasize a noun',
                    'They are <code>such</code> heroes.'
                  ]
                ]
              }
            ],
            examples: [
              'The movie was <code>so</code> interesting.',
              "It's <code>such</code> a beautiful day.",
              'The book was <code>so</code> exciting.',
              'She is <code>such</code> a talented musician.'
            ]
          },
          error: null
        },
        {
          name: 'Some Any',
          slug: 'some-any',
          rule: {
            slug: 'some-any',
            title: 'Some and Any',
            description:
              '<code>Some</code> and <code>any</code> are used to talk about an unspecified amount or number of things. Use <code>some</code> in positive sentences and <code>any</code> in negative sentences or questions.',
            tables: [
              {
                caption: 'How to use some and any',
                columns: ['Word', 'Usage', 'Example'],
                rows: [
                  [
                    'some',
                    'Positive sentences',
                    'I have <code>some</code> apples.'
                  ],
                  [
                    'any',
                    'Negative sentences',
                    "I don't have <code>any</code> apples."
                  ],
                  ['any', 'Questions', 'Do you have <code>any</code> apples?']
                ]
              }
            ],
            examples: [
              'There are <code>some</code> books on the table.',
              "We didn't find <code>any</code> mistakes.",
              'Is there <code>any</code> milk left?',
              'She wants <code>some</code> water.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Passive Voice',
      slug: 'passive-voice',
      topics: [
        {
          name: 'Future Perfect Passive',
          slug: 'future-perfect-passive',
          rule: {
            slug: 'future-perfect-passive',
            title: 'Future Perfect Passive',
            description:
              'The future perfect passive is used to show that an action will be completed by a certain time in the future. It is formed with <code>will have been</code> followed by a past participle. This tense is often used to focus on the action rather than who performs it.',
            tables: [
              {
                caption: 'Structure of Future Perfect Passive',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'will have been + past participle',
                    'Used for actions completed by a future time',
                    'The work <code>will have been completed</code> by tomorrow.'
                  ],
                  [
                    'negative form',
                    'Add <code>not</code> after <code>will</code>',
                    'The work <code>will not have been finished</code> by next week.'
                  ],
                  [
                    'question form',
                    'Place <code>will</code> before the subject',
                    '<code>Will</code> the project <code>have been done</code> by Friday?'
                  ]
                ]
              }
            ],
            examples: [
              'The new bridge <code>will have been built</code> by next year.',
              'By the time you arrive, dinner <code>will have been prepared</code>.',
              'All the tickets <code>will have been sold</code> by tomorrow morning.'
            ]
          },
          error: null
        },
        {
          name: 'Future Simple Passive',
          slug: 'future-simple-passive',
          rule: {
            slug: 'future-simple-passive',
            title: 'Future Simple Passive',
            description:
              'The future simple passive is used to show that something will be done by someone or something else. It focuses on the action itself rather than who performs it. The structure is <code>will be + past participle</code>.',
            tables: [
              {
                caption: 'Forming the future simple passive',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Positive',
                    '<code>will be + past participle</code>',
                    'The book <code>will be read</code> by her.'
                  ],
                  [
                    'Negative',
                    '<code>will not be + past participle</code>',
                    'The task <code>will not be completed</code> on time.'
                  ],
                  [
                    'Question',
                    '<code>Will + subject + be + past participle?</code>',
                    '<code>Will</code> the work <code>be finished</code> by tomorrow?'
                  ]
                ]
              }
            ],
            examples: [
              'The project <code>will be completed</code> by next week.',
              'Dinner <code>will be served</code> at 7 PM.',
              '<code>Will</code> the room <code>be cleaned</code> by the staff?'
            ]
          },
          error: null
        },
        {
          name: 'Passive Voice',
          slug: 'passive-voice',
          rule: {
            slug: 'passive-voice',
            title: 'Passive Voice',
            description:
              'The passive voice is used when the focus is on the action, not who is performing it. It is formed with <code>be</code> + past participle. The subject of the sentence receives the action.',
            tables: [
              {
                caption: 'Forming the passive voice',
                columns: ['Tense', 'Form', 'Example'],
                rows: [
                  [
                    'Present Simple',
                    '<code>is/are</code> + past participle',
                    '<code>The book is</code> read by Mary.'
                  ],
                  [
                    'Past Simple',
                    '<code>was/were</code> + past participle',
                    '<code>The letter was</code> sent yesterday.'
                  ],
                  [
                    'Future Simple',
                    '<code>will be</code> + past participle',
                    '<code>The work will be</code> finished soon.'
                  ]
                ]
              },
              {
                caption: 'Passive voice examples with different tenses',
                columns: [
                  'Tense',
                  'Example Sentence',
                  'Active Voice Equivalent'
                ],
                rows: [
                  [
                    'Present Continuous',
                    '<code>The house is being</code> painted.',
                    'They are painting the house.'
                  ],
                  [
                    'Present Perfect',
                    '<code>The song has been</code> sung.',
                    'She has sung the song.'
                  ],
                  [
                    'Past Continuous',
                    '<code>The cake was being</code> baked.',
                    'They were baking the cake.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>The car was</code> repaired by the mechanic.',
              '<code>The movie is being</code> watched by the class.',
              '<code>The email has been</code> sent.',
              '<code>The dinner will be</code> cooked by John.'
            ]
          },
          error: null
        },
        {
          name: 'Past Continuous Passive',
          slug: 'past-continuous-passive',
          rule: {
            slug: 'past-continuous-passive',
            title: 'Past Continuous Passive',
            description:
              'The past continuous passive is used to describe actions that were happening at a specific time in the past. It focuses on the action itself rather than who was doing it. Form it with <code>was/were being</code> + past participle.',
            tables: [
              {
                caption: 'Forming the past continuous passive',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'was being',
                    'Singular subject',
                    'The letter <code>was being written</code>.'
                  ],
                  [
                    'were being',
                    'Plural subject',
                    'The letters <code>were being written</code>.'
                  ],
                  [
                    'was being',
                    'Singular subject',
                    'The cake <code>was being baked</code>.'
                  ]
                ]
              }
            ],
            examples: [
              'Dinner <code>was being served</code> when we arrived.',
              'The song <code>was being played</code> loudly.',
              'New roads <code>were being built</code> last year.'
            ]
          },
          error: null
        },
        {
          name: 'Past Perfect Passive',
          slug: 'past-perfect-passive',
          rule: {
            slug: 'past-perfect-passive',
            title: 'Past Perfect Passive',
            description:
              'The past perfect passive is used to show that something had been done by someone before another action in the past. It is formed with <code>had been</code> followed by the past participle of the verb.',
            tables: [
              {
                caption: 'Forming the past perfect passive',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'had been + past participle',
                    'Used to show completed action',
                    'The cake <code>had been eaten</code> before I arrived.'
                  ],
                  [
                    'had been + past participle',
                    'Emphasizes action done by someone',
                    'The letter <code>had been sent</code> by John.'
                  ],
                  [
                    'had been + past participle',
                    'Shows cause and effect in the past',
                    'The room <code>had been cleaned</code> before the guests came.'
                  ]
                ]
              }
            ],
            examples: [
              'The documents <code>had been prepared</code> before the meeting started.',
              'The car <code>had been repaired</code> by the mechanic.',
              'All tickets <code>had been sold</code> by the time we arrived.'
            ]
          },
          error: null
        },
        {
          name: 'Past Simple Passive',
          slug: 'past-simple-passive',
          rule: {
            slug: 'past-simple-passive',
            title: 'Past Simple Passive',
            description:
              'The past simple passive is used when the focus is on the action, not who performed it. It is formed with <code>was</code> or <code>were</code> + past participle of the verb. Use this structure to talk about actions completed in the past.',
            tables: [
              {
                caption: 'Forming the Past Simple Passive',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'Singular noun',
                    '<code>was</code> + past participle',
                    'The book <code>was written</code> by the author.'
                  ],
                  [
                    'Plural noun',
                    '<code>were</code> + past participle',
                    'The houses <code>were built</code> in 1990.'
                  ],
                  [
                    'Uncountable noun',
                    '<code>was</code> + past participle',
                    'Water <code>was used</code> to clean the floor.'
                  ]
                ]
              }
            ],
            examples: [
              'The letter <code>was sent</code> yesterday.',
              'The paintings <code>were sold</code> last week.',
              'The report <code>was finished</code> on time.'
            ]
          },
          error: null
        },
        {
          name: 'Present Continuous Passive',
          slug: 'present-continuous-passive',
          rule: {
            slug: 'present-continuous-passive',
            title: 'Present Continuous Passive',
            description:
              'The present continuous passive is used to describe actions that are happening now and are being done by someone. It is formed with <code>is being</code> or <code>are being</code> followed by the past participle of the verb.',
            tables: [
              {
                caption: 'Forming the present continuous passive',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'Singular',
                    '<code>is being</code> + past participle',
                    '<code>The cake is being baked</code> now.'
                  ],
                  [
                    'Plural',
                    '<code>are being</code> + past participle',
                    '<code>The cars are being washed</code> today.'
                  ],
                  [
                    'Negative',
                    '<code>is/are not being</code> + past participle',
                    '<code>The room is not being cleaned</code> at the moment.'
                  ]
                ]
              }
            ],
            examples: [
              'The homework <code>is being done</code> by the students.',
              'Lunch <code>is being served</code> in the cafeteria.',
              'The letters <code>are being written</code> by the secretary.'
            ]
          },
          error: null
        },
        {
          name: 'Present Perfect Passive',
          slug: 'present-perfect-passive',
          rule: {
            slug: 'present-perfect-passive',
            title: 'Present Perfect Passive',
            description:
              'The present perfect passive is used to focus on the action and its result rather than who did it. It is formed with <code>has been</code> or <code>have been</code> plus the past participle of the verb. This tense shows actions that happened at an unspecified time and are relevant now.',
            tables: [
              {
                caption: 'Forming the present perfect passive',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'I/You/We/They',
                    'have been + past participle',
                    'The book <code>has been read</code>.'
                  ],
                  [
                    'He/She/It',
                    'has been + past participle',
                    'The letter <code>has been sent</code>.'
                  ]
                ]
              }
            ],
            examples: [
              'The car <code>has been washed</code>.',
              'All the cookies <code>have been eaten</code>.',
              'The movie <code>has been watched</code> many times.'
            ]
          },
          error: null
        },
        {
          name: 'Present Simple Passive',
          slug: 'present-simple-passive',
          rule: {
            slug: 'present-simple-passive',
            title: 'Present Simple Passive',
            description:
              'The present simple passive is used to show that the action is done by someone or something else. It is formed with <code>is</code> or <code>are</code> + the past participle of the verb. Use it when the focus is on the action, not who does it.',
            tables: [
              {
                caption: 'Forming the Present Simple Passive',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'Singular noun',
                    'is + past participle',
                    '<code>The book is read</code> by many students.'
                  ],
                  [
                    'Plural noun',
                    'are + past participle',
                    '<code>Letters are sent</code> every day.'
                  ],
                  [
                    'Uncountable noun',
                    'is + past participle',
                    '<code>Water is used</code> for drinking.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>The house is cleaned</code> every week.',
              '<code>Cars are manufactured</code> in this factory.',
              '<code>Rice is grown</code> in many countries.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Past Future',
      slug: 'past-future',
      topics: [
        {
          name: 'Past Future Continuous',
          slug: 'past-future-continuous',
          rule: {
            slug: 'past-future-continuous',
            title: 'Past Future Continuous',
            description:
              'The past future continuous tense describes an action that would be happening at a certain time in the past. It is formed with <code>would be</code> followed by the <code>-ing</code> form of the verb. This tense is often used in reported speech or when imagining a situation in the past.',
            tables: [
              {
                caption: 'Forming the past future continuous',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'would be + verb-ing',
                    'Used for an action that was expected to be happening',
                    'He said he <code>would be traveling</code> at that time.'
                  ],
                  [
                    'would not be + verb-ing',
                    'Negative form',
                    'She <code>would not be attending</code> the meeting.'
                  ],
                  [
                    'would be + subject + verb-ing?',
                    'Question form',
                    '<code>Would</code> you <code>be working</code> then?'
                  ]
                ]
              }
            ],
            examples: [
              'They <code>would be playing</code> football in the afternoon.',
              'By 5 PM, she <code>would be finishing</code> her homework.',
              'He asked if I <code>would be joining</code> them for dinner.'
            ]
          },
          error: null
        },
        {
          name: 'Past Future Perfect',
          slug: 'past-future-perfect',
          rule: {
            slug: 'past-future-perfect',
            title: 'Past Future Perfect',
            description:
              'The past future perfect tense describes an action that would have been completed before another action in the past. It uses <code>would have</code> plus the past participle of the verb.',
            tables: [
              {
                caption: 'Forming the past future perfect tense',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Affirmative',
                    '<code>would have</code> + past participle',
                    'She <code>would have finished</code> the project.'
                  ],
                  [
                    'Negative',
                    '<code>would not have</code> + past participle',
                    'He <code>would not have seen</code> the movie.'
                  ],
                  [
                    'Question',
                    '<code>would</code> + subject + <code>have</code> + past participle?',
                    'Would they <code>have arrived</code> by then?'
                  ]
                ]
              }
            ],
            examples: [
              'By 5 PM, they <code>would have left</code> the office.',
              'He <code>would have completed</code> his homework before dinner.',
              'She asked if we <code>would have finished</code> by Monday.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Past Tense',
      slug: 'past-tense',
      topics: [
        {
          name: 'Past Continuous',
          slug: 'past-continuous',
          rule: {
            slug: 'past-continuous',
            title: 'Past Continuous',
            description:
              'The past continuous tense describes actions that were ongoing in the past. It is formed using <code>was/were</code> + the present participle (<code>-ing</code> form). Use it to set the scene or talk about interrupted actions.',
            tables: [
              {
                caption: 'How to form the past continuous',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'I/He/She/It',
                    '<code>was</code> + verb + <code>-ing</code>',
                    'I <code>was eating</code> when you called.'
                  ],
                  [
                    'You/We/They',
                    '<code>were</code> + verb + <code>-ing</code>',
                    'They <code>were playing</code> football.'
                  ]
                ]
              }
            ],
            examples: [
              'She <code>was reading</code> a book at 8 PM.',
              'We <code>were watching</code> TV when the lights went out.',
              'He <code>was not listening</code> to the teacher.',
              'What <code>were</code> you <code>doing</code> at that time?'
            ]
          },
          error: null
        },
        {
          name: 'Past Perfect Continuous',
          slug: 'past-perfect-continuous',
          rule: {
            slug: 'past-perfect-continuous',
            title: 'Past Perfect Continuous',
            description:
              'The past perfect continuous tense is used to show that an action started in the past and continued up to another point in the past. It uses <code>had been</code> followed by a verb ending in <code>-ing</code>.',
            tables: [
              {
                caption: 'Forming the Past Perfect Continuous',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Positive',
                    '<code>had been</code> + <code>verb-ing</code>',
                    'She <code>had been studying</code> all night.'
                  ],
                  [
                    'Negative',
                    '<code>had not been</code> + <code>verb-ing</code>',
                    'They <code>had not been waiting</code> long.'
                  ],
                  [
                    'Question',
                    '<code>Had</code> + subject + <code>been</code> + <code>verb-ing</code>?',
                    '<code>Had</code> he <code>been working</code> there?'
                  ]
                ]
              }
            ],
            examples: [
              'By the time we arrived, she <code>had been sleeping</code> for two hours.',
              'He <code>had not been feeling</code> well before the meeting.',
              '<code>Had</code> they <code>been playing</code> outside when it started to rain?'
            ]
          },
          error: null
        },
        {
          name: 'Past Perfect',
          slug: 'past-perfect',
          rule: {
            slug: 'past-perfect',
            title: 'Past Perfect',
            description:
              'The past perfect tense shows that one action happened before another in the past. It is formed with <code>had</code> plus the past participle of the verb. Use it to make clear the order of past events.',
            tables: [
              {
                caption: 'Forming the Past Perfect Tense',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Affirmative',
                    '<code>had</code> + past participle',
                    'She <code>had left</code> before he arrived.'
                  ],
                  [
                    'Negative',
                    '<code>had not</code> + past participle',
                    'They <code>had not seen</code> the movie before.'
                  ],
                  [
                    'Question',
                    '<code>Had</code> + subject + past participle?',
                    '<code>Had</code> you <code>finished</code> the work?'
                  ]
                ]
              }
            ],
            examples: [
              'By the time we arrived, she <code>had already left</code>.',
              'He <code>had finished</code> his homework before dinner.',
              'They <code>hadn’t met</code> before the party.'
            ]
          },
          error: null
        },
        {
          name: 'Past Simple',
          slug: 'past-simple',
          rule: {
            slug: 'past-simple',
            title: 'Past Simple',
            description:
              'The past simple tense describes actions that happened at a specific time in the past. Regular verbs add <code>-ed</code> to the base form, while irregular verbs have unique forms. Use it to talk about completed events.',
            tables: [
              {
                caption: 'Regular Verb Forms in Past Simple',
                columns: ['Base Form', 'Past Simple', 'Example'],
                rows: [
                  ['walk', 'walked', 'I <code>walked</code> to school.'],
                  [
                    'play',
                    'played',
                    'She <code>played</code> tennis yesterday.'
                  ],
                  ['watch', 'watched', 'We <code>watched</code> a movie.']
                ]
              },
              {
                caption: 'Irregular Verb Forms in Past Simple',
                columns: ['Base Form', 'Past Simple', 'Example'],
                rows: [
                  ['go', 'went', 'They <code>went</code> to the market.'],
                  ['have', 'had', 'I <code>had</code> breakfast early.'],
                  ['eat', 'ate', 'He <code>ate</code> an apple.']
                ]
              }
            ],
            examples: [
              'She <code>visited</code> her grandmother last week.',
              'We <code>traveled</code> to France last summer.',
              'He <code>bought</code> a new car yesterday.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Present Tense',
      slug: 'present-tense',
      topics: [
        {
          name: 'Present Continuous',
          slug: 'present-continuous',
          rule: {
            slug: 'present-continuous',
            title: 'Present Continuous',
            description:
              'The present continuous tense is used to describe actions happening now or around the current time. It is formed with <code>am/is/are</code> + <code>verb</code> ending in <code>-ing</code>. Use this tense to talk about temporary situations.',
            tables: [
              {
                caption: 'Forming the present continuous',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  ['I', 'am + verb-ing', 'I am <code>reading</code>.'],
                  [
                    'He/She/It',
                    'is + verb-ing',
                    'She is <code>running</code>.'
                  ],
                  [
                    'We/You/They',
                    'are + verb-ing',
                    'They are <code>playing</code>.'
                  ]
                ]
              }
            ],
            examples: [
              'I am <code>writing</code> a letter.',
              'He is <code>watching</code> TV.',
              'We are <code>having</code> dinner now.'
            ]
          },
          error: null
        },
        {
          name: 'Present Perfect Continuous',
          slug: 'present-perfect-continuous',
          rule: {
            slug: 'present-perfect-continuous',
            title: 'Present Perfect Continuous',
            description:
              'The present perfect continuous tense is used to show that an action started in the past and is still continuing now. It uses <code>has been</code> or <code>have been</code> together with the base verb ending in <code>-ing</code>.',
            tables: [
              {
                caption: 'Forming the Present Perfect Continuous',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'I/You/We/They',
                    '<code>have been</code> + verb<code>-ing</code>',
                    'I <code>have been studying</code> for two hours.'
                  ],
                  [
                    'He/She/It',
                    '<code>has been</code> + verb<code>-ing</code>',
                    'She <code>has been working</code> all morning.'
                  ]
                ]
              }
            ],
            examples: [
              'They <code>have been playing</code> soccer since noon.',
              'He <code>has been reading</code> a book for an hour.',
              'We <code>have been waiting</code> for the bus.'
            ]
          },
          error: null
        },
        {
          name: 'Present Perfect',
          slug: 'present-perfect',
          rule: {
            slug: 'present-perfect',
            title: 'Present Perfect',
            description:
              "The present perfect tense connects past actions to the present. Use <code>have</code> or <code>has</code> with the past participle of the verb. It's often used with words like <code>just</code>, <code>already</code>, and <code>yet</code>.",
            tables: [
              {
                caption: 'Forming the Present Perfect',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'I/You/We/They',
                    '<code>have</code> + past participle',
                    'I <code>have eaten</code> lunch.'
                  ],
                  [
                    'He/She/It',
                    '<code>has</code> + past participle',
                    'She <code>has finished</code> her homework.'
                  ]
                ]
              },
              {
                caption: 'Using Present Perfect with Time Words',
                columns: ['Time Word', 'Usage', 'Example'],
                rows: [
                  [
                    'just',
                    'For recent actions',
                    'I <code>have just seen</code> the movie.'
                  ],
                  [
                    'already',
                    'For actions done before now',
                    'They <code>have already left</code>.'
                  ],
                  [
                    'yet',
                    'In questions or negatives',
                    'Have you <code>finished</code> your work <code>yet</code>?'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>have visited</code> Paris.',
              'He <code>has lived</code> here for two years.',
              'We <code>have just met</code> the new teacher.',
              'Have you <code>ever been</code> to London?',
              'She <code>has not finished</code> her dinner yet.'
            ]
          },
          error: null
        },
        {
          name: 'Present Simple',
          slug: 'present-simple',
          rule: {
            slug: 'present-simple',
            title: 'Present Simple',
            description:
              'The present simple tense is used for actions that are regular, habitual, or generally true. We add <code>-s</code> or <code>-es</code> for third person singular. Use the base form of the verb for all other subjects.',
            tables: [
              {
                caption: 'Forming the present simple',
                columns: ['Subject', 'Verb Form', 'Example'],
                rows: [
                  ['I/You/We/They', 'Base form', 'I <code>play</code> tennis.'],
                  [
                    'He/She/It',
                    'Base form + <code>-s</code>',
                    'She <code>plays</code> tennis.'
                  ],
                  [
                    'He/She/It',
                    'Base form + <code>-es</code>',
                    'He <code>watches</code> TV.'
                  ]
                ]
              },
              {
                caption: 'Usage of present simple',
                columns: ['Usage', 'Explanation', 'Example'],
                rows: [
                  [
                    'Habitual action',
                    'Something done regularly',
                    'They <code>go</code> to school every day.'
                  ],
                  [
                    'General truth',
                    'Something always true',
                    'The sun <code>rises</code> in the east.'
                  ],
                  [
                    'Permanent situation',
                    'Something that is always the same',
                    'He <code>lives</code> in Paris.'
                  ]
                ]
              }
            ],
            examples: [
              'We <code>study</code> English every Monday.',
              'He <code>works</code> at a bank.',
              'Water <code>boils</code> at 100 degrees Celsius.',
              'She <code>does</code> her homework in the evening.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Pronoun',
      slug: 'pronoun',
      topics: [
        {
          name: 'Indefinite Pronouns',
          slug: 'indefinite-pronouns',
          rule: {
            slug: 'indefinite-pronouns',
            title: 'Indefinite Pronouns',
            description:
              'Indefinite pronouns are used to refer to people or things without saying exactly who or what they are. Words like <code>someone</code>, <code>anyone</code>, <code>something</code>, and <code>anything</code> are examples. They help us talk about things in a general way.',
            tables: [
              {
                caption: 'Common indefinite pronouns',
                columns: ['Pronoun', 'Usage', 'Example'],
                rows: [
                  [
                    'someone',
                    'Refers to an unspecified person',
                    '<code>Someone</code> is at the door.'
                  ],
                  [
                    'anyone',
                    'Refers to any person at all',
                    '<code>Anyone</code> can join the club.'
                  ],
                  [
                    'something',
                    'Refers to an unspecified thing',
                    'I need <code>something</code> to drink.'
                  ],
                  [
                    'anything',
                    'Refers to any thing at all',
                    'Do you want <code>anything</code> from the store?'
                  ],
                  [
                    'none',
                    'Refers to not one or nothing',
                    '<code>None</code> of these options is good.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Someone</code> left their umbrella here.',
              'Is there <code>anything</code> I can do to help?',
              '<code>None</code> of the answers were correct.',
              "He doesn't want <code>anyone</code> to know.",
              'We need <code>something</code> to eat.'
            ]
          },
          error: null
        },
        {
          name: 'Objective Pronouns',
          slug: 'objective-pronouns',
          rule: {
            slug: 'objective-pronouns',
            title: 'Objective Pronouns',
            description:
              'Objective pronouns receive the action in a sentence. Use them to replace nouns after verbs or prepositions. Examples include <code>me</code>, <code>him</code>, <code>her</code>, <code>us</code>, and <code>them</code>.',
            tables: [
              {
                caption: 'Objective pronouns and their usage',
                columns: ['Pronoun', 'Usage', 'Example'],
                rows: [
                  [
                    'me',
                    'First person singular',
                    'She called <code>me</code>.'
                  ],
                  [
                    'you',
                    'Second person singular/plural',
                    'I will help <code>you</code>.'
                  ],
                  [
                    'him',
                    'Third person singular masculine',
                    'We saw <code>him</code>.'
                  ],
                  [
                    'her',
                    'Third person singular feminine',
                    'They talked to <code>her</code>.'
                  ],
                  [
                    'it',
                    'Third person singular neutral',
                    'Can you move <code>it</code>?'
                  ],
                  ['us', 'First person plural', 'He invited <code>us</code>.'],
                  [
                    'them',
                    'Third person plural',
                    'She gave <code>them</code> a gift.'
                  ]
                ]
              }
            ],
            examples: [
              'Please give <code>me</code> the book.',
              'We met <code>her</code> at the store.',
              'Tell <code>them</code> the truth.',
              'Can you hear <code>it</code>?',
              'He showed <code>us</code> the way.'
            ]
          },
          error: null
        },
        {
          name: 'Personal Pronouns',
          slug: 'personal-pronouns',
          rule: {
            slug: 'personal-pronouns',
            title: 'Personal Pronouns',
            description:
              'Personal pronouns are used to replace specific nouns in a sentence. They can represent a person, a group, or a thing. Examples include <code>he</code>, <code>she</code>, <code>it</code>, <code>we</code>, and <code>they</code>.',
            tables: [
              {
                caption: 'Types of Personal Pronouns',
                columns: ['Pronoun', 'Usage', 'Example'],
                rows: [
                  ['I', 'First person singular', '<code>I</code> am happy.'],
                  [
                    'you',
                    'Second person singular/plural',
                    '<code>You</code> are late.'
                  ],
                  [
                    'he',
                    'Third person singular (male)',
                    '<code>He</code> is a teacher.'
                  ],
                  [
                    'she',
                    'Third person singular (female)',
                    '<code>She</code> is my friend.'
                  ],
                  [
                    'it',
                    'Third person singular (thing/animal)',
                    '<code>It</code> is raining.'
                  ],
                  [
                    'we',
                    'First person plural',
                    '<code>We</code> are going home.'
                  ],
                  [
                    'they',
                    'Third person plural',
                    '<code>They</code> are playing.'
                  ]
                ]
              }
            ],
            examples: [
              '<code>She</code> likes ice cream.',
              '<code>They</code> went to the zoo.',
              '<code>We</code> are studying English.',
              '<code>It</code> is a sunny day.'
            ]
          },
          error: null
        },
        {
          name: 'Possessive Adjectives',
          slug: 'possessive-adjectives',
          rule: {
            slug: 'possessive-adjectives',
            title: 'Possessive Adjectives',
            description:
              'Possessive adjectives show who owns something. They come before a noun to indicate possession. Examples include <code>my</code>, <code>your</code>, and <code>his</code>.',
            tables: [
              {
                caption: 'Common possessive adjectives',
                columns: ['Possessive Adjective', 'Owner', 'Example'],
                rows: [
                  ['my', 'I', 'This is <code>my</code> book.'],
                  ['your', 'You', 'Is that <code>your</code> hat?'],
                  ['his', 'He', '<code>His</code> car is fast.'],
                  ['her', 'She', '<code>Her</code> dress is blue.'],
                  ['its', 'It', '<code>Its</code> tail is long.'],
                  ['our', 'We', '<code>Our</code> house is big.'],
                  ['their', 'They', '<code>Their</code> dog is friendly.']
                ]
              }
            ],
            examples: [
              'This is <code>my</code> pen.',
              'Is that <code>your</code> notebook?',
              '<code>His</code> shoes are new.',
              '<code>Her</code> smile is bright.',
              'The cat licked <code>its</code> paw.',
              '<code>Our</code> team won the match.',
              '<code>Their</code> garden is beautiful.'
            ]
          },
          error: null
        },
        {
          name: 'Possessive Pronouns',
          slug: 'possessive-pronouns',
          rule: {
            slug: 'possessive-pronouns',
            title: 'Possessive Pronouns',
            description:
              'Possessive pronouns show ownership and do not need a noun after them. Words like <code>mine</code>, <code>yours</code>, and <code>theirs</code> replace nouns to indicate who something belongs to.',
            tables: [
              {
                caption: 'Common possessive pronouns',
                columns: ['Pronoun', 'Used For', 'Example'],
                rows: [
                  [
                    'mine',
                    'First person singular',
                    'This book is <code>mine</code>.'
                  ],
                  [
                    'yours',
                    'Second person singular/plural',
                    'Is this pen <code>yours</code>?'
                  ],
                  [
                    'his',
                    'Third person singular (male)',
                    'The hat is <code>his</code>.'
                  ],
                  [
                    'hers',
                    'Third person singular (female)',
                    'The scarf is <code>hers</code>.'
                  ],
                  [
                    'ours',
                    'First person plural',
                    'The victory is <code>ours</code>.'
                  ],
                  [
                    'theirs',
                    'Third person plural',
                    'The house is <code>theirs</code>.'
                  ]
                ]
              }
            ],
            examples: [
              'This bike is <code>mine</code>.',
              'Is that bag <code>yours</code>?',
              'The coat is <code>hers</code>.',
              'The decision was <code>theirs</code>.'
            ]
          },
          error: null
        },
        {
          name: 'Reflexive Pronouns',
          slug: 'reflexive-pronouns',
          rule: {
            slug: 'reflexive-pronouns',
            title: 'Reflexive Pronouns',
            description:
              'Reflexive pronouns are used when the subject and the object of the sentence are the same. They end with <code>-self</code> or <code>-selves</code> and match the subject in number and gender.',
            tables: [
              {
                caption: 'Reflexive pronouns for each subject',
                columns: ['Subject', 'Reflexive Pronoun', 'Example'],
                rows: [
                  [
                    'I',
                    '<code>myself</code>',
                    'I taught <code>myself</code> to play guitar.'
                  ],
                  [
                    'you (singular)',
                    '<code>yourself</code>',
                    'Did you make this cake <code>yourself</code>?'
                  ],
                  [
                    'he',
                    '<code>himself</code>',
                    'He looked at <code>himself</code> in the mirror.'
                  ],
                  [
                    'she',
                    '<code>herself</code>',
                    'She prepared <code>herself</code> for the exam.'
                  ],
                  [
                    'it',
                    '<code>itself</code>',
                    'The cat cleaned <code>itself</code>.'
                  ],
                  [
                    'we',
                    '<code>ourselves</code>',
                    'We organized the event <code>ourselves</code>.'
                  ],
                  [
                    'you (plural)',
                    '<code>yourselves</code>',
                    'You all can help <code>yourselves</code> to snacks.'
                  ],
                  [
                    'they',
                    '<code>themselves</code>',
                    'They cooked dinner <code>themselves</code>.'
                  ]
                ]
              }
            ],
            examples: [
              'She made the dress <code>herself</code>.',
              'The dog opened the door <code>itself</code>.',
              'We painted the house <code>ourselves</code>.',
              'He fixed the bike <code>himself</code>.'
            ]
          },
          error: null
        },
        {
          name: 'Subjective Pronouns',
          slug: 'subjective-pronouns',
          rule: {
            slug: 'subjective-pronouns',
            title: 'Subjective Pronouns',
            description:
              'Subjective pronouns are used as the subject of a sentence. They include <code>I</code>, <code>you</code>, <code>he</code>, <code>she</code>, <code>it</code>, <code>we</code>, and <code>they</code>. These words tell us who is doing the action in a sentence.',
            tables: [
              {
                caption: 'Subjective pronouns and their usage',
                columns: ['Pronoun', 'Singular/Plural', 'Example'],
                rows: [
                  ['I', 'Singular', '<code>I</code> am a student.'],
                  ['you', 'Singular/Plural', '<code>You</code> are my friend.'],
                  ['he', 'Singular', '<code>He</code> likes football.'],
                  ['she', 'Singular', '<code>She</code> is a teacher.'],
                  ['it', 'Singular', '<code>It</code> is raining.'],
                  ['we', 'Plural', '<code>We</code> are going to the park.'],
                  ['they', 'Plural', '<code>They</code> have a meeting.']
                ]
              }
            ],
            examples: [
              '<code>She</code> is reading a book.',
              '<code>They</code> are playing soccer.',
              '<code>We</code> will go to the museum tomorrow.'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Question Form',
      slug: 'question-form',
      topics: [
        {
          name: 'Be Going To Question',
          slug: 'be-going-to-question',
          rule: {
            slug: 'be-going-to-question',
            title: 'Be Going To Questions',
            description:
              'Use <code>be going to</code> for questions about future plans or predictions. Start with <code>am</code>, <code>is</code>, or <code>are</code>, followed by the subject and <code>going to</code>.',
            tables: [
              {
                caption: "Forming questions with 'be going to'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Am',
                    'Start question with <code>am</code>',
                    'Am I <code>going to</code> eat?'
                  ],
                  [
                    'Is',
                    'Start question with <code>is</code>',
                    'Is she <code>going to</code> travel?'
                  ],
                  [
                    'Are',
                    'Start question with <code>are</code>',
                    'Are they <code>going to</code> play?'
                  ]
                ]
              }
            ],
            examples: [
              'Is he <code>going to</code> join us?',
              'Are you <code>going to</code> watch the movie?',
              'Am I <code>going to</code> need my umbrella?'
            ]
          },
          error: null
        },
        {
          name: 'Future Simple Question',
          slug: 'future-simple-question',
          rule: {
            slug: 'future-simple-question',
            title: 'Future Simple Question',
            description:
              'The future simple is used to ask about actions or events that will happen in the future. Form a question with <code>will</code> followed by the subject and the base form of the verb.',
            tables: [
              {
                caption: 'Forming future simple questions',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'will + subject + base verb',
                    'Basic structure',
                    '<code>Will</code> you <code>go</code>?'
                  ],
                  [
                    'will + I/we + base verb',
                    "Use with 'I' or 'we'",
                    '<code>Will</code> we <code>see</code>?'
                  ],
                  [
                    'will + he/she/it/they + base verb',
                    "Use with 'he', 'she', 'it', 'they'",
                    '<code>Will</code> she <code>come</code>?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Will</code> they <code>arrive</code> on time?',
              '<code>Will</code> you <code>help</code> me with this?',
              '<code>Will</code> it <code>rain</code> tomorrow?'
            ]
          },
          error: null
        },
        {
          name: 'Have Has Got Question',
          slug: 'have-has-got-question',
          rule: {
            slug: 'have-has-got-question',
            title: 'Have/Has Got Questions',
            description:
              'We use <code>have got</code> or <code>has got</code> to talk about possession or relationships. In questions, start with <code>have</code> or <code>has</code>, then add the subject, and follow with <code>got</code>.',
            tables: [
              {
                caption: 'Forming questions with have/has got',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Have',
                    'Used with I/you/we/they',
                    '<code>Have</code> you <code>got</code> a pen?'
                  ],
                  [
                    'Has',
                    'Used with he/she/it',
                    '<code>Has</code> she <code>got</code> a car?'
                  ],
                  [
                    'Negative',
                    'Add <code>not</code> after <code>have/has</code>',
                    'Have you <code>not got</code> any paper?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Have</code> they <code>got</code> a new house?',
              '<code>Has</code> he <code>got</code> any siblings?',
              '<code>Have</code> you <code>got</code> a moment to talk?'
            ]
          },
          error: null
        },
        {
          name: 'Past Perfect Continuous Question',
          slug: 'past-perfect-continuous-question',
          rule: {
            slug: 'past-perfect-continuous-question',
            title: 'Past Perfect Continuous Questions',
            description:
              'Use the past perfect continuous tense to ask about actions that were ongoing before another action in the past. Form questions with <code>had</code> + <code>been</code> + <code>verb</code> + <code>ing</code>.',
            tables: [
              {
                caption: 'Structure of past perfect continuous questions',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Had + subject + been + verb+ing',
                    'Question structure',
                    '<code>Had</code> she <code>been working</code>?'
                  ],
                  [
                    'Wh-word + had + subject + been + verb+ing',
                    "Questions with 'wh' words",
                    '<code>Why</code> had they <code>been studying</code>?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Had</code> you <code>been waiting</code> long before she arrived?',
              '<code>Where</code> had he <code>been living</code> before he moved here?',
              '<code>Had</code> they <code>been traveling</code> for many months?'
            ]
          },
          error: null
        },
        {
          name: 'Past Simple Question',
          slug: 'past-simple-question',
          rule: {
            slug: 'past-simple-question',
            title: 'Past Simple Questions',
            description:
              'To ask questions in the past simple, use <code>did</code> followed by the subject and the base form of the verb. This form is used for all subjects.',
            tables: [
              {
                caption: 'Forming past simple questions',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Did + subject + base verb',
                    'Use for all subjects',
                    '<code>Did</code> you <code>see</code> the movie?'
                  ],
                  [
                    'Did + he/she/it + base verb',
                    'Use for third person singular',
                    '<code>Did</code> she <code>go</code> to school?'
                  ],
                  [
                    'Did + we/you/they + base verb',
                    'Use for plural subjects',
                    '<code>Did</code> they <code>finish</code> the project?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Did</code> they <code>find</code> the keys?',
              '<code>Did</code> you <code>call</code> her yesterday?',
              '<code>Did</code> he <code>play</code> football last weekend?'
            ]
          },
          error: null
        },
        {
          name: 'Present Continuous Question',
          slug: 'present-continuous-question',
          rule: {
            slug: 'present-continuous-question',
            title: 'Present Continuous Questions',
            description:
              'Present continuous questions ask about actions happening now. They start with <code>am</code>, <code>is</code>, or <code>are</code> followed by the subject and the <code>-ing</code> form of the verb.',
            tables: [
              {
                caption: 'Forming Present Continuous Questions',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  ['Am I', "Used with 'I'", '<code>Am I</code> talking?'],
                  [
                    'Is he/she/it',
                    "Used with 'he', 'she', 'it'",
                    '<code>Is she</code> eating?'
                  ],
                  [
                    'Are we/you/they',
                    "Used with 'we', 'you', 'they'",
                    '<code>Are they</code> coming?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Is</code> he <code>reading</code> a book now?',
              '<code>Are</code> you <code>watching</code> TV?',
              '<code>Am</code> I <code>doing</code> this correctly?'
            ]
          },
          error: null
        },
        {
          name: 'Present Perfect Question',
          slug: 'present-perfect-question',
          rule: {
            slug: 'present-perfect-question',
            title: 'Present Perfect Question',
            description:
              'Present perfect questions ask about actions that happened at an indefinite time in the past or have relevance now. Use <code>have</code> or <code>has</code> followed by the subject and the past participle of the verb.',
            tables: [
              {
                caption: 'Forming Present Perfect Questions',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Have + subject + past participle',
                    'For I/you/we/they',
                    '<code>Have</code> you <code>seen</code> the movie?'
                  ],
                  [
                    'Has + subject + past participle',
                    'For he/she/it',
                    '<code>Has</code> she <code>finished</code> her homework?'
                  ],
                  [
                    'Have + subject + past participle',
                    'For plural nouns',
                    '<code>Have</code> the students <code>arrived</code>?'
                  ]
                ]
              }
            ],
            examples: [
              'Have you <code>ever visited</code> Paris?',
              'Has he <code>finished</code> the project?',
              'Have they <code>eaten</code> breakfast yet?'
            ]
          },
          error: null
        },
        {
          name: 'Present Simple Question',
          slug: 'present-simple-question',
          rule: {
            slug: 'present-simple-question',
            title: 'Present Simple Questions',
            description:
              'Present simple questions are used to ask about habits, facts, or general truths. Use <code>do</code> or <code>does</code> at the beginning of the question. Remember to use the base form of the main verb after <code>do</code> or <code>does</code>.',
            tables: [
              {
                caption: 'Forming present simple questions',
                columns: ['Subject', 'Question Start', 'Example'],
                rows: [
                  [
                    'I/You/We/They',
                    '<code>Do</code>',
                    '<code>Do</code> you like coffee?'
                  ],
                  [
                    'He/She/It',
                    '<code>Does</code>',
                    '<code>Does</code> she play tennis?'
                  ]
                ]
              }
            ],
            examples: [
              '<code>Do</code> they need help?',
              '<code>Does</code> he understand the lesson?',
              '<code>Do</code> we have class today?'
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Used To',
      slug: 'used-to',
      topics: [
        {
          name: 'Be Used To',
          slug: 'be-used-to',
          rule: {
            slug: 'be-used-to',
            title: 'Be Used To',
            description:
              'The phrase <code>be used to</code> describes being familiar with something or being accustomed to it. It is followed by a noun or a gerund (<code>-ing</code> form). Use it to show comfort with a habit or situation.',
            tables: [
              {
                caption: "Structure of 'Be Used To'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'be used to + noun',
                    'Familiar with a thing',
                    'She is <code>used to</code> the noise.'
                  ],
                  [
                    'be used to + gerund',
                    'Familiar with an action',
                    'They are <code>used to</code> waking up early.'
                  ],
                  [
                    'be used to + noun/gerund',
                    'Past or present familiarity',
                    'He was <code>used to</code> spicy food.'
                  ]
                ]
              }
            ],
            examples: [
              'I am <code>used to</code> the cold weather.',
              'She is <code>used to</code> driving in the city.',
              'They are <code>used to</code> working late hours.'
            ]
          },
          error: null
        },
        {
          name: 'Get Used To',
          slug: 'get-used-to',
          rule: {
            slug: 'get-used-to',
            title: 'Get Used To',
            description:
              'We use <code>get used to</code> to talk about becoming familiar with something over time. It describes the process of adjusting to new situations or habits.',
            tables: [
              {
                caption: "Forming 'Get Used To'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'get used to + noun',
                    'Used with nouns',
                    'He will <code>get used to</code> the noise.'
                  ],
                  [
                    'get used to + gerund',
                    'Used with gerunds',
                    'She is <code>getting used to</code> waking up early.'
                  ],
                  [
                    'got used to + noun/gerund',
                    'Past tense',
                    'They <code>got used to</code> the new rules quickly.'
                  ]
                ]
              }
            ],
            examples: [
              'I need to <code>get used to</code> this new phone.',
              "He can't <code>get used to</code> working at night.",
              'She is <code>getting used to</code> the cold weather.'
            ]
          },
          error: null
        },
        {
          name: 'Used To',
          slug: 'used-to',
          rule: {
            slug: 'used-to',
            title: 'Used To',
            description:
              'The phrase <code>used to</code> describes actions or situations that were true in the past but are not true now. It is often used to talk about past habits or states.',
            tables: [
              {
                caption: "Structure of 'used to'",
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  [
                    'used to',
                    'Past habits',
                    'I <code>used to</code> play soccer every weekend.'
                  ],
                  [
                    'used to',
                    'Past states',
                    'She <code>used to</code> live in Spain.'
                  ],
                  [
                    "didn't use to",
                    'Negative form',
                    "He <code>didn't use to</code> like coffee."
                  ],
                  [
                    'Did ... use to?',
                    'Question form',
                    '<code>Did</code> you <code>use to</code> work here?'
                  ]
                ]
              }
            ],
            examples: [
              'We <code>used to</code> go to the beach every summer.',
              '<code>Did</code> she <code>use to</code> have long hair?',
              "They <code>didn't use to</code> eat meat."
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Verb',
      slug: 'verb',
      topics: [
        {
          name: 'Auxiliary Verbs',
          slug: 'auxiliary-verbs',
          rule: {
            slug: 'auxiliary-verbs',
            title: 'Auxiliary Verbs',
            description:
              "Auxiliary verbs help the main verb to show tense, mood, or voice. Common auxiliary verbs are <code>be</code>, <code>have</code>, and <code>do</code>. They are also known as 'helper verbs.'",
            tables: [
              {
                caption: 'Common auxiliary verbs and their uses',
                columns: ['Auxiliary Verb', 'Usage', 'Example'],
                rows: [
                  [
                    'be',
                    'To form continuous tenses',
                    '<code>is</code> running'
                  ],
                  ['have', 'To form perfect tenses', '<code>has</code> eaten'],
                  [
                    'do',
                    'To form questions and negatives',
                    '<code>do</code> not know'
                  ]
                ]
              }
            ],
            examples: [
              'She <code>is</code> watching TV.',
              'They <code>have</code> finished their homework.',
              '<code>Do</code> you like ice cream?',
              'He <code>does</code> not understand the question.'
            ]
          },
          error: null
        },
        {
          name: 'Gerund',
          slug: 'gerund',
          rule: {
            slug: 'gerund',
            title: 'Gerunds',
            description:
              'A gerund is a verb form that ends in <code>-ing</code> and functions as a noun. We use gerunds after certain verbs, prepositions, and as subjects in sentences. They express activities or actions in a general way.',
            tables: [
              {
                caption: 'Common uses of gerunds',
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  [
                    'Verb + gerund',
                    'After certain verbs',
                    'He enjoys <code>swimming</code>.'
                  ],
                  [
                    'Preposition + gerund',
                    'After prepositions',
                    'She is good at <code>painting</code>.'
                  ],
                  [
                    'Gerund as subject',
                    'At the start of a sentence',
                    '<code>Running</code> is fun.'
                  ]
                ]
              }
            ],
            examples: [
              'I love <code>reading</code> books.',
              'They talked about <code>traveling</code> to Italy.',
              '<code>Cooking</code> is his favorite hobby.'
            ]
          },
          error: null
        },
        {
          name: 'Had Better',
          slug: 'had-better',
          rule: {
            slug: 'had-better',
            title: 'Had Better',
            description:
              'Use <code>had better</code> to give strong advice or recommendations. It is similar to <code>should</code> but more urgent. The structure is <code>had better</code> + base verb.',
            tables: [
              {
                caption: "Form and usage of 'had better'",
                columns: ['Form', 'Usage', 'Example'],
                rows: [
                  [
                    'had better',
                    'Strong advice',
                    'You <code>had better</code> study for the test.'
                  ],
                  [
                    'had better not',
                    'Strong negative advice',
                    'You <code>had better not</code> be late.'
                  ],
                  [
                    'had better',
                    'Urgent recommendation',
                    'We <code>had better</code> leave now.'
                  ]
                ]
              }
            ],
            examples: [
              "You <code>had better</code> wear a coat. It's cold outside.",
              'They <code>had better</code> finish their homework before dinner.',
              'She <code>had better not</code> forget her keys.'
            ]
          },
          error: null
        },
        {
          name: 'Have Has Got',
          slug: 'have-has-got',
          rule: {
            slug: 'have-has-got',
            title: 'Have/Has Got',
            description:
              'We use <code>have got</code> or <code>has got</code> to talk about possessions or relationships. Use <code>have got</code> with <code>I</code>, <code>you</code>, <code>we</code>, and <code>they</code>. Use <code>has got</code> with <code>he</code>, <code>she</code>, and <code>it</code>.',
            tables: [
              {
                caption: 'Forms of Have/Has Got',
                columns: ['Subject', 'Form', 'Example'],
                rows: [
                  [
                    'I/You/We/They',
                    '<code>have got</code>',
                    'I <code>have got</code> a car.'
                  ],
                  [
                    'He/She/It',
                    '<code>has got</code>',
                    'She <code>has got</code> a cat.'
                  ]
                ]
              }
            ],
            examples: [
              'We <code>have got</code> a new house.',
              'He <code>has got</code> a brother.',
              'They <code>have got</code> a lot of friends.',
              'She <code>has got</code> a nice dress.'
            ]
          },
          error: null
        },
        {
          name: 'Infinitive Gerund',
          slug: 'infinitive-gerund',
          rule: {
            slug: 'infinitive-gerund',
            title: 'Infinitive and Gerund',
            description:
              'In English, verbs can follow other verbs in two forms: <code>to + base form</code> (infinitive) or <code>verb + ing</code> (gerund). The choice depends on the first verb and the meaning of the sentence.',
            tables: [
              {
                caption: 'Common verbs followed by infinitive or gerund',
                columns: ['Verb', 'Infinitive', 'Gerund'],
                rows: [
                  ['like', 'to like', 'liking'],
                  ['want', 'to want', 'wanting'],
                  ['enjoy', 'to enjoy', 'enjoying'],
                  ['decide', 'to decide', 'deciding'],
                  ['finish', 'to finish', 'finishing']
                ]
              },
              {
                caption: 'When to use infinitive or gerund',
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'Infinitive',
                    'After certain verbs',
                    'I want <code>to eat</code>.'
                  ],
                  [
                    'Infinitive',
                    'To express purpose',
                    'She went out <code>to buy</code> milk.'
                  ],
                  ['Gerund', 'As a subject', '<code>Swimming</code> is fun.'],
                  [
                    'Gerund',
                    'After prepositions',
                    'He is good at <code>playing</code> chess.'
                  ]
                ]
              }
            ],
            examples: [
              'I enjoy <code>reading</code> books.',
              'They decided <code>to leave</code> early.',
              'She is interested in <code>learning</code> French.',
              'He promised <code>to help</code> me.'
            ]
          },
          error: null
        },
        {
          name: 'Infinitive',
          slug: 'infinitive',
          rule: {
            slug: 'infinitive',
            title: 'Infinitive',
            description:
              'The infinitive form of a verb is its base form, often preceded by <code>to</code>. It is used to express purpose, after certain verbs, or as a subject or object in a sentence.',
            tables: [
              {
                caption: 'Common uses of the infinitive',
                columns: ['Use', 'Rule', 'Example'],
                rows: [
                  [
                    'Purpose',
                    'To show intention',
                    'He went to the store <code>to buy</code> milk.'
                  ],
                  [
                    'After certain verbs',
                    'Used with verbs like <code>want</code>, <code>need</code>',
                    'She wants <code>to learn</code> Spanish.'
                  ],
                  [
                    'As a subject',
                    'Acts as the subject of a sentence',
                    '<code>To travel</code> is fun.'
                  ],
                  [
                    'As an object',
                    'Acts as the object of a verb',
                    'He likes <code>to swim</code>.'
                  ]
                ]
              }
            ],
            examples: [
              'I need <code>to finish</code> my homework.',
              'They plan <code>to visit</code> Europe next summer.',
              '<code>To understand</code> him, you need patience.',
              'She decided <code>to call</code> her friend.'
            ]
          },
          error: null
        },
        {
          name: 'Make Do',
          slug: 'make-do',
          rule: {
            slug: 'make-do',
            title: 'Make vs. Do',
            description:
              'Use <code>make</code> for creating or producing something and <code>do</code> for activities, tasks, or work. Knowing when to use each word helps in making sentences clearer.',
            tables: [
              {
                caption: 'When to use Make and Do',
                columns: ['Verb', 'Usage', 'Example'],
                rows: [
                  ['make', 'Creating or producing', '<code>make</code> a cake'],
                  [
                    'make',
                    'Planning or deciding',
                    '<code>make</code> a decision'
                  ],
                  ['do', 'General activities', '<code>do</code> homework'],
                  ['do', 'Non-specific tasks', '<code>do</code> the dishes']
                ]
              }
            ],
            examples: [
              'I need to <code>make</code> a phone call.',
              'She likes to <code>do</code> yoga every morning.',
              'We will <code>make</code> plans for the weekend.',
              'He promised to <code>do</code> his chores after school.'
            ]
          },
          error: null
        },
        {
          name: 'Say Tell',
          slug: 'say-tell',
          rule: {
            slug: 'say-tell',
            title: 'Say vs. Tell',
            description:
              'Use <code>say</code> when you want to express words or information. Use <code>tell</code> when you are giving someone information directly. <code>Tell</code> is often followed by a person.',
            tables: [
              {
                caption: 'Difference between Say and Tell',
                columns: ['Verb', 'Usage', 'Example'],
                rows: [
                  [
                    'say',
                    'Follow with what is said',
                    'He <code>said</code> hello.'
                  ],
                  [
                    'tell',
                    'Follow with who is told',
                    'She <code>told</code> me a story.'
                  ],
                  [
                    'say',
                    'Do not follow with a person',
                    'They <code>said</code> that it was raining.'
                  ],
                  [
                    'tell',
                    'Directly followed by a person',
                    'Please <code>tell</code> John the news.'
                  ]
                ]
              }
            ],
            examples: [
              'He <code>said</code> he would come.',
              'Can you <code>tell</code> her the time?',
              'She <code>said</code> goodbye when she left.',
              'I will <code>tell</code> them the truth.'
            ]
          },
          error: null
        },
        {
          name: 'Tag Questions',
          slug: 'tag-questions',
          rule: {
            slug: 'tag-questions',
            title: 'Tag Questions',
            description:
              "Tag questions are short questions added to the end of a statement to confirm information or ask for agreement. They often use <code>isn't it</code>, <code>don't you</code>, or similar forms, changing according to the verb and subject of the main sentence.",
            tables: [
              {
                caption: 'Forming tag questions',
                columns: ['Main sentence', 'Auxiliary verb', 'Tag question'],
                rows: [
                  ['You are coming', 'are', "aren't you?"],
                  ['She likes coffee', 'does', "doesn't she?"],
                  ["He can't swim", 'can', 'can he?'],
                  ['We should go', 'should', "shouldn't we?"]
                ]
              }
            ],
            examples: [
              "It's a nice day, <code>isn't it</code>?",
              "You don't like that movie, <code>do you</code>?",
              "They have finished, <code>haven't they</code>?",
              "She isn't ready, <code>is she</code>?"
            ]
          },
          error: null
        }
      ]
    },
    {
      name: 'Would',
      slug: 'would',
      topics: [
        {
          name: 'Would Like',
          slug: 'would-like',
          rule: {
            slug: 'would-like',
            title: 'Would Like',
            description:
              'Use <code>would like</code> to talk about things you want in a polite way. It is often used for making requests or offers. The structure is <code>would like + noun/verb</code>.',
            tables: [
              {
                caption: "Forming requests with 'would like'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'would like + noun',
                    'To express a desire for something',
                    'I <code>would like</code> a coffee.'
                  ],
                  [
                    'would like + to + verb',
                    'To express a desire to do something',
                    'She <code>would like to</code> visit Paris.'
                  ],
                  [
                    'would you like',
                    'To make an offer or invitation',
                    '<code>Would you like</code> some tea?'
                  ]
                ]
              }
            ],
            examples: [
              'He <code>would like</code> a new phone.',
              'We <code>would like to</code> watch a movie.',
              '<code>Would you like</code> to join us for dinner?'
            ]
          },
          error: null
        },
        {
          name: 'Would Prefer',
          slug: 'would-prefer',
          rule: {
            slug: 'would-prefer',
            title: 'Would Prefer',
            description:
              'Use <code>would prefer</code> to talk about choices or things you like more at a specific time. It is often used with verbs in the base form or with nouns. This helps show polite preferences.',
            tables: [
              {
                caption: "Forming sentences with 'would prefer'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'would prefer + to + verb',
                    'For specific actions',
                    'I <code>would prefer to</code> stay home.'
                  ],
                  [
                    'would prefer + noun',
                    'For specific things',
                    'She <code>would prefer</code> coffee.'
                  ],
                  [
                    'would prefer + verb-ing',
                    'Alternative form',
                    'They <code>would prefer</code> going early.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>would prefer to</code> sit by the window.',
              'He <code>would prefer</code> tea over coffee.',
              'We <code>would prefer</code> leaving tomorrow.'
            ]
          },
          error: null
        },
        {
          name: 'Would Rather',
          slug: 'would-rather',
          rule: {
            slug: 'would-rather',
            title: 'Would Rather',
            description:
              '<code>Would rather</code> is used to express a preference between two actions or choices. It is often followed by the base form of a verb. Use <code>would rather</code> in a sentence to show which option you prefer.',
            tables: [
              {
                caption: "Structure of 'would rather'",
                columns: ['Form', 'Rule', 'Example'],
                rows: [
                  [
                    'would rather + base verb',
                    'Used to show preference',
                    'I <code>would rather</code> stay home.'
                  ],
                  [
                    'would rather + subject + past verb',
                    "Talking about another person's preference",
                    'She <code>would rather</code> he <code>came</code> early.'
                  ]
                ]
              }
            ],
            examples: [
              'I <code>would rather</code> eat pasta than rice.',
              'He <code>would rather</code> not go to the party.',
              'They <code>would rather</code> you <code>visited</code> tomorrow.'
            ]
          },
          error: null
        }
      ]
    }
  ]
}
